import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Plus, Copy, Search, Filter, Download, Edit, Trash2, Eye, CheckCircle, Send, Printer, Mail, Clock, Package, RefreshCw, Undo2, ChevronDown, ShieldCheck, User, Building, Calendar, QrCode, ExternalLink } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { EmailComposeModal, ActivityHistoryModal, DoubleScrollbar } from '../../components/UI';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import DeliveryInvoicePrintView from './DeliveryInvoicePrintView';
import WarrantyCardPrintView from '../Warranty/WarrantyCardPrintView';
import { Html5Qrcode } from 'html5-qrcode';
import Button from '../../components/UI/Button';
import { printContent, prepareContentForPrint, restoreContentAfterPrint } from '../../utils/printUtils';
import { printInvoice } from '../../utils/simplePrintUtils';
import { printWarrantyCard, printAllWarrantyCards } from '../../utils/warrantyPrintUtils';
import PrintPreviewModal from '../../components/UI/PrintPreviewModal';
import JsBarcode from 'jsbarcode';
import { ErrorHandler } from '../../utils/errorHandler';

interface Customer {
  _id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
}

interface Project {
  _id: string;
  name: string;
  address?: string;
  projectAddress?: string;
  workOrders?: { number: string; date: string }[];
  customerName?: string;
}

interface Employee {
  _id: string;
  name: string;
  phone?: string;
}

interface Category {
  _id: string;
  name: string;
  parentCategory?: string;
  description?: string;
}

interface Product {
  _id: string;
  name: string;
  barcode?: string;
  unit?: string;
  units?: string[];
  price?: number;
  sku?: string;
  description?: string;
  category?: {
    _id: string;
    name: string;
  };
  customFields?: {
    brand?: string;
    model?: string;
    sku?: string;
    type?: string;
    compressorType?: string;
    countryOfOrigin?: string;
    countryOfAssembly?: string;
    details?: string;
    description?: string;
    capacity?: string;
    specifications?: string | object;
    serialNo?: string;
    partNo?: string;
    specification?: string;
  };
  specifications?: string | Record<string, any>;
  stockQuantity?: number; // Added for stock check
}

interface InvoiceProduct {
  productId: string;
  barcode: string;
  name: string;
  unit?: string;
  price?: number;
  qty?: number;
  amount?: number;
  discount?: number;
  total?: number;
  sku?: string;
  description?: string;
  customFields?: {
    brand?: string;
    model?: string;
    sku?: string;
    type?: string;
    compressorType?: string;
    countryOfOrigin?: string;
    countryOfAssembly?: string;
    details?: string;
    description?: string;
    capacity?: string;
    specifications?: string | object;
    serialNo?: string;
    serialNumbers?: string[];
    partNo?: string;
    specification?: string;
  };
  specifications?: string | Record<string, any>;
  brand?: string;
  model?: string;
  type?: string;
  compressorType?: string;
  countryOfOrigin?: string;
  countryOfAssembly?: string;
  details?: string;
  capacity?: string;
  serialNo?: string;
  serialNumbers?: string[];
  partNo?: string;
  specification?: string;
  remarks?: string;
  warrantyDetails?: {
    hasWarranty: boolean;
    serviceWarranty?: number | null;
    sparePartsWarranty?: number | null;
    compressorWarranty?: number | null;
    warranty?: number | null;
  };
}

export interface DeliveryInvoice {
  _id: string;
  invoiceNumber: string;
  type: string;
  status: string;
  customer: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  project?: {
    _id: string;
    name: string;
    address?: string;
  };
  workOrder?: {
    number?: string;
    date?: string;
  };
  deliveryAddress?: string;
  dispatchedThrough?: {
    name: string;
    phone?: string;
  };
  items: Array<{
    product?: {
      _id: string;
      name: string;
      description?: string;
      unit?: string;
    };
    name?: string;
    brand?: string;
    model?: string;
    sku?: string;
    type?: string;
    compressorType?: string;
    countryOfOrigin?: string;
    countryOfAssembly?: string;
    details?: string;
    description?: string;
    capacity?: string;
    serialNo?: string;
    serialNumbers?: string[];
    partNo?: string;
    specification?: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    totalPrice: number;
    warrantyDetails?: {
      hasWarranty: boolean;
      serviceWarranty?: number | null;
      sparePartsWarranty?: number | null;
      compressorWarranty?: number | null;
      warranty?: number | null;
    };
  }>;
  subtotal: number;
  discount: number;
  total: number;
  createdAt: string;
  date?: string;
}

async function fetchNextDeliveryInvoiceNumber(): Promise<string> {
  const response = await apiService.get('/invoices/next-number?type=delivery');
  return response.data.invoiceNumber;
}

// Add prop type for handlePrint
interface DeliveryInvoicesPageProps {
  handlePrint: (invoice: any, type: 'purchase' | 'delivery') => void;
}

interface WarrantyItem {
  _id: string;
  customer: { _id: string; name: string; email?: string; phone?: string };
  project: { _id: string; name: string; address?: string };
  workOrder: { number: string; date: string };
  deliveryInvoice?: { _id: string; invoiceNumber: string; date?: string; createdAt?: string };
  product: { _id: string; name: string; brand?: string; model?: string; sku?: string; barcode?: string; customFields?: any };
  brand?: string;
  brandLogo?: string;
  model?: string;
  type?: string;
  serialNo?: string;
  compressorType?: string;
  notes?: string;
  quantity?: number;
  unit?: string;
  serviceWarrantyYears?: number;
  sparePartsWarrantyYears?: number;
  compressorWarrantyYears?: number;
  warrantyYears?: number;
  warrantyStartDate: string;
  serviceWarrantyEndDate?: string;
  sparePartsWarrantyEndDate?: string;
  compressorWarrantyEndDate?: string;
  warrantyEndDate?: string;
  serviceDaysRemaining?: number;
  sparePartsDaysRemaining?: number;
  compressorDaysRemaining?: number;
  daysRemaining?: number;
  status: 'active' | 'expired' | 'expiring_soon';
}

const DeliveryInvoicesPage: React.FC<DeliveryInvoicesPageProps> = ({ handlePrint }) => {
  const { user, token, isAdmin } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  
  // State management
  const [invoices, setInvoices] = useState<DeliveryInvoice[]>([]);
  const [returnInvoices, setReturnInvoices] = useState<any[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [loadingWarranties, setLoadingWarranties] = useState(false);
  const [invoiceWarranties, setInvoiceWarranties] = useState<WarrantyItem[]>([]);
  const [allWarranties, setAllWarranties] = useState<WarrantyItem[]>([]);
  const [hasWarrantyMap, setHasWarrantyMap] = useState<Record<string, boolean>>({});
  const [editingInvoice, setEditingInvoice] = useState<DeliveryInvoice | null>(null);
  const [viewInvoice, setViewInvoice] = useState<DeliveryInvoice | null>(null);
  const [selectedInvoiceForWarranty, setSelectedInvoiceForWarranty] = useState<DeliveryInvoice | null>(null);
  const [printCurrentPage, setPrintCurrentPage] = useState(1);
  const [showPrintOverlay, setShowPrintOverlay] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [selectedInvoiceForPrint, setSelectedInvoiceForPrint] = useState<DeliveryInvoice | null>(null);
  const [selectedWarrantyForPrint, setSelectedWarrantyForPrint] = useState<WarrantyItem | null>(null);
  const [showWarrantyPrintPreview, setShowWarrantyPrintPreview] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [selectedInvoicesForPrint, setSelectedInvoicesForPrint] = useState<DeliveryInvoice[]>([]);
  const [showMultiplePrintPreview, setShowMultiplePrintPreview] = useState(false);

  const itemsPerPage = 14;

  // New invoice state
  const [newInvoice, setNewInvoice] = useState({
    invoiceNumber: '',
    date: getTodayDateString(),
    customerId: '',
    customerAddress: '',
    customerPhone: '',
    customerEmail: '',
    projectId: '',
    projectAddress: '',
    workOrderNumber: '',
    workOrderDate: '',
    dispatchedThroughId: '',
    dispatchedThroughPhone: '',
    deliveryAddress: '',
    products: [
      {
        productId: '',
        barcode: '',
        name: '',
        unit: '',
        qty: 1,
        price: 0,
        amount: 0,
        discount: 0,
        total: 0,
        remarks: '',
        warrantyDetails: {
          hasWarranty: false,
          serviceWarranty: null,
          sparePartsWarranty: null,
          compressorWarranty: null,
          warranty: null
        }
      }
    ]
  });

  // Add state for selected work order
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<{ number: string; date: string } | null>(null);
  const [addedProducts, setAddedProducts] = useState<InvoiceProduct[]>([]);
  const [currentProduct, setCurrentProduct] = useState<InvoiceProduct>({
    productId: '',
    barcode: '',
    name: '',
    unit: '',
    qty: 1,
    price: 0,
            amount: 0,
            discount: 0,
            total: 0,
            remarks: '',
            serialNumbers: [],
            warrantyDetails: {      hasWarranty: false,
      serviceWarranty: null,
      sparePartsWarranty: null,
      compressorWarranty: null,
      warranty: null
    }
  });

  // State for searchable product dropdown
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const productInputRef = useRef<HTMLInputElement>(null);
  const [selectedParentCategory, setSelectedParentCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  // Add state to track which fields have been focused
  const [focusedFields, setFocusedFields] = useState<Set<string>>(new Set());

  const [formErrors, setFormErrors] = useState<{ customer?: string; project?: string; date?: string; products: string[] }>({ customer: '', project: '', date: '', products: [] });
  const [saving, setSaving] = useState(false);

  // Load from Purchase Invoice (Add Delivery Invoice modal)
  interface PurchaseInvoiceOption {
    _id: string;
    invoiceNumber: string;
    date?: string;
    createdAt?: string;
    customerName?: string;
    customer?: { name?: string };
    items: Array<{
      product?: { _id: string } | string | null;
      productId?: string;
      barcode?: string;
      name?: string;
      unit?: string;
      qty?: number;
      quantity?: number;
      purchaseRate?: number;
      unitPrice?: number;
      amount?: number;
      discount?: number;
      total?: number;
      totalPrice?: number;
      brand?: string;
      model?: string;
      sku?: string;
      type?: string;
      compressorType?: string;
      countryOfOrigin?: string;
      countryOfAssembly?: string;
      details?: string;
      description?: string;
      capacity?: string;
      serialNo?: string;
      partNo?: string;
      remarks?: string;
    }>;
  }
  const [purchaseInvoicesForLoad, setPurchaseInvoicesForLoad] = useState<PurchaseInvoiceOption[]>([]);
  const [loadingPurchaseInvoices, setLoadingPurchaseInvoices] = useState(false);
  const [selectedPurchaseInvoiceId, setSelectedPurchaseInvoiceId] = useState<string>('');

  // Add state for email modal
  const [emailModalInvoice, setEmailModalInvoice] = useState<DeliveryInvoice | null>(null);
  const [sending, setSending] = useState(false);
  const [showActivityHistoryModal, setShowActivityHistoryModal] = useState(false);
  const [selectedInvoiceForHistory, setSelectedInvoiceForHistory] = useState<DeliveryInvoice | null>(null);

  // Add state for editing a product in the addedProducts list
  const [editProductIdx, setEditProductIdx] = useState<number | null>(null);
  const [editProductData, setEditProductData] = useState<InvoiceProduct | null>(null);

  // Add Product Modal States
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState('');
  const [createModalData, setCreateModalData] = useState<any>({ name: '', description: '', website: '', code: '', region: '' });
  const [newProduct, setNewProduct] = useState({
    productName: '',
    model: '',
    brand: '',
    category: '',
    location: '',
    barcodeNo: '',
    generatedBarcode: '',
    price: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    unitId: '',
    unitIds: [] as string[],
    image: '',
    status: 'active',
    description: '',
    type: '',
    compressorType: '',
    countryOfOrigin: '',
    countryOfAssembly: '',
    specification: '',
    details: '',
    serialNo: '',
    partNo: '',
    sku: ''
  });
  const [deliveryAddProductUnitsDropdownOpen, setDeliveryAddProductUnitsDropdownOpen] = useState(false);
  const deliveryAddProductUnitsDropdownRef = useRef<HTMLDivElement>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [compressorTypes, setCompressorTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [rootCategories, setRootCategories] = useState<Category[]>([]);
  const [barcodeImage, setBarcodeImage] = useState<string | null>(null);
  const barcodeCanvasRef = useRef<HTMLCanvasElement>(null);
  // Handler to open edit modal
  const handleEditProduct = (idx: number) => {
    setEditProductIdx(idx);
    setEditProductData({ ...addedProducts[idx] });
  };
  // Handler to update edit form fields
  const handleEditProductChange = (field: keyof InvoiceProduct, value: any) => {
    setEditProductData(prev => prev ? { ...prev, [field]: value } : prev);
  };
  // Handler to save edited product
  const handleSaveEditProduct = () => {
    if (editProductIdx === null || !editProductData) return;
    const product = products.find(p => p._id === editProductData.productId);
    if (!product) return;
    const oldQty = addedProducts[editProductIdx]?.qty || 0;
    const newQty = Number(editProductData.qty) || 0;
    // Calculate available stock after edit
    const availableStock = (product.stockQuantity ?? 0) + oldQty - newQty;
    if (availableStock < 0) {
      setInsufficientStockWarning('Insufficient Stock');
      return;
    }
    setInsufficientStockWarning(null);
    if (editProductIdx !== null && editProductData) {
      setAddedProducts(prev => prev.map((p, i) => i === editProductIdx ? { ...editProductData, amount: (editProductData.qty ?? 1) * (editProductData.price ?? 0), total: (editProductData.qty ?? 1) * (editProductData.price ?? 0) - (editProductData.discount || 0) } : p));
      setEditProductIdx(null);
      setEditProductData(null);
    }
  };
  // Handler to close edit modal
  const handleCloseEditProduct = () => {
    setEditProductIdx(null);
    setEditProductData(null);
  };

  // Add state for insufficient stock warning
  const [insufficientStockWarning, setInsufficientStockWarning] = useState<string | null>(null);

  // Pending filter state (for UI controls)
  const [pendingFilterCustomer, setPendingFilterCustomer] = useState('');
  const [pendingFilterProject, setPendingFilterProject] = useState('');
  const [pendingFilterStartDate, setPendingFilterStartDate] = useState('');
  const [pendingFilterEndDate, setPendingFilterEndDate] = useState('');
  const [pendingFilterStatus, setPendingFilterStatus] = useState('');

  // Filter state (applied when Filter button is clicked)
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Add BarcodeScanner state and ref
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const scannerRef = useRef<any>(null);



  // Helper functions
  function getTodayDateString() {
    return new Date().toISOString().split('T')[0];
  }

  function safeCurrency(val: any) {
    if (val === null || val === undefined || val === '') return '৳0.00';
    const num = parseFloat(val);
    if (isNaN(num)) return '৳0.00';
    return `৳${num.toFixed(2)}`;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'blank': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to check if invoice is blank (all items have no product)
  const isInvoiceBlank = (invoice: DeliveryInvoice) => {
    if (!invoice.items || invoice.items.length === 0) return false;
    return invoice.items.every((item: any) => !item.product || (typeof item.product === 'object' && !item.product._id));
  };

  // Helper function to get effective status (override to 'blank' if all items are blank)
  const getEffectiveStatus = (invoice: DeliveryInvoice) => {
    if (isInvoiceBlank(invoice)) return 'blank';
    return invoice.status;
  };

  // Data fetching
  useEffect(() => {
    const fetchAll = async () => {
      // console.log('🚀 Starting fetchAll (initial load)...');
      setLoading(true);
      try {
        const [invRes, returnInvRes, custRes, projRes, empRes, prodRes, categoriesRes, rootCategoriesRes, locationsRes, brandsRes, typesRes, compressorTypesRes, countriesRes, unitsRes, warrantyRes] = await Promise.all([
          apiService.get('/invoices?type=delivery'),
          apiService.get('/invoices?type=return'),
          apiService.get('/customers'),
          apiService.get('/projects'),
          apiService.get('/employees'),
          apiService.get('/products?limit=1000'),
          apiService.get('/categories/all'),
          apiService.get('/categories/root'),
          apiService.get('/locations/all'),
          apiService.get('/brands/all'),
          apiService.get('/product-types/all'),
          apiService.get('/compressor-types/all'),
          apiService.get('/countries/all'),
          apiService.get('/units/all'),
          apiService.get('/warranties'),
        ]);
        setInvoices(invRes.data.invoices || []);
        setReturnInvoices(returnInvRes.data.invoices || []);
        
        // Build warranty map
        const warranties = warrantyRes.data || [];
        setAllWarranties(warranties);
        const map: Record<string, boolean> = {};
        warranties.forEach((w: any) => {
          if (w.deliveryInvoice?._id || w.deliveryInvoice) {
            map[w.deliveryInvoice?._id || w.deliveryInvoice] = true;
          }
        });
        setHasWarrantyMap(map);

        setCustomers(custRes.data.customers || []);
        setProjects(projRes.data.projects || []);
        setEmployees(empRes.data.employees || []);
        setProducts(prodRes.data.products || []);
        setCategories(categoriesRes.data || []);
        setRootCategories(rootCategoriesRes.data || []);
        
        // Set add product modal data
        setLocations(locationsRes.data || []);
        setBrands(brandsRes.data || []);
        setTypes(typesRes.data || []);
        setCompressorTypes(compressorTypesRes.data || []);
        setCountries(countriesRes.data || []);
        setUnits(unitsRes.data || []);
        
        // Separate parent and sub categories
        const allCategories = categoriesRes.data || [];
        const parents = allCategories.filter((cat: Category) => !cat.parentCategory);
        const subs = allCategories.filter((cat: Category) => cat.parentCategory);
        setParentCategories(parents);
        setSubCategories(subs);
        
      } catch (err) {
        toast.error('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Fetch next invoice number when opening Add modal
  useEffect(() => {
    if (showAddModal) {
      fetchNextDeliveryInvoiceNumber()
        .then(invoiceNumber => setNewInvoice(prev => ({ ...prev, invoiceNumber })))
        .catch(() => setNewInvoice(prev => ({ ...prev, invoiceNumber: 'DINV-0001001' })));
    }
  }, [showAddModal]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (deliveryAddProductUnitsDropdownRef.current && !deliveryAddProductUnitsDropdownRef.current.contains(e.target as Node)) {
        setDeliveryAddProductUnitsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch purchase invoices when Add modal opens (for Load from Purchase Invoice), sorted latest to oldest
  useEffect(() => {
    if (!showAddModal) {
      setSelectedPurchaseInvoiceId('');
      return;
    }
    let cancelled = false;
    setLoadingPurchaseInvoices(true);
    apiService
      .get('/invoices?type=purchase&limit=500')
      .then((res: any) => {
        if (cancelled) return;
        const list: PurchaseInvoiceOption[] = (res.data.invoices || []).slice();
        list.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0).getTime();
          const dateB = new Date(b.date || b.createdAt || 0).getTime();
          return dateB - dateA;
        });
        setPurchaseInvoicesForLoad(list);
      })
      .catch(() => {
        if (!cancelled) setPurchaseInvoicesForLoad([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPurchaseInvoices(false);
      });
    return () => { cancelled = true; };
  }, [showAddModal]);

  // Get unique statuses from invoices
  const uniqueStatuses = Array.from(new Set(invoices.map(inv => inv.status).filter(Boolean)));

  // Get selected customer name for filtering projects
  const selectedCustomerObj = customers.find(c => c._id === pendingFilterCustomer);
  const selectedCustomerName = selectedCustomerObj ? selectedCustomerObj.name : '';
  // Filtered projects for selected customer by customerName
  const filteredProjects = pendingFilterCustomer
    ? projects.filter(p => p.customerName === selectedCustomerName)
    : [];

  // Filtered invoices
  const selectedCustomerObjForFilter = customers.find(c => c._id === filterCustomer);
  const selectedCustomerNameForFilter = selectedCustomerObjForFilter ? selectedCustomerObjForFilter.name : '';
  const filteredInvoices = invoices.filter(inv => {
    let pass = true;
    // Customer filter
    if (filterCustomer && inv.customer?.name !== selectedCustomerNameForFilter) pass = false;
    // Project filter (only if project is selected)
    if (filterCustomer && filterProject && inv.project?._id !== filterProject) pass = false;
    // Status filter
    if (filterStatus && inv.status !== filterStatus) pass = false;
    // Period filter (by invoice date or createdAt, compare only date part)
    const invoiceDateStr = (inv.date || inv.createdAt || '').slice(0, 10); // 'YYYY-MM-DD'
    if (filterStartDate && invoiceDateStr < filterStartDate) pass = false;
    if (filterEndDate && invoiceDateStr > filterEndDate) pass = false;
    return pass;
  });

  // Apply search filter to the already filtered invoices
  const searchFilteredInvoices = filteredInvoices.filter(inv => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      inv.customer?.name?.toLowerCase().includes(searchLower) ||
      inv.invoiceNumber?.toLowerCase().includes(searchLower) ||
      inv.project?.name?.toLowerCase().includes(searchLower) ||
      inv.customer?.email?.toLowerCase().includes(searchLower) ||
      inv.customer?.phone?.toLowerCase().includes(searchLower)
    );
  });

  // Event handlers
  const handleViewInvoice = (invoice: DeliveryInvoice) => {
    setViewInvoice(invoice);
    setShowViewModal(true);
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await apiService.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleExportInvoices = async () => {
    try {
      await apiService.download('/invoices?type=delivery&export=excel', 'delivery-invoices.xlsx');
    } catch (error) {
      toast.error('Failed to export invoices');
    }
  };

  const fetchInvoices = async () => {
    console.log('🚀 Starting fetchInvoices...');
    try {
      console.log('📥 Fetching delivery invoices...');
      const response = await apiService.get('/invoices?type=delivery');
      setInvoices(response.data.invoices || []);
      console.log('✅ Delivery invoices fetched successfully');
      
      // Also fetch return invoices to detect return invoices
      console.log('📥 Fetching return invoices...');
      try {
        console.log('Attempting to fetch return invoices...');
        const returnResponse = await apiService.get('/invoices?type=return');
        const returnInvoicesData = returnResponse.data.invoices || [];
        setReturnInvoices(returnInvoicesData);
        console.log('✅ Return invoices fetched successfully');
        
        // Debug logging
        console.log('Fetched delivery invoices:', response.data.invoices || []);
        console.log('Fetched return invoices:', returnInvoicesData);
        console.log('Total delivery invoices found:', (response.data.invoices || []).length);
        console.log('Total return invoices found:', returnInvoicesData.length);
        
        // Log each return invoice with its delivery invoice reference
        returnInvoicesData.forEach((returnInv: any) => {
          console.log('Return invoice:', {
            id: returnInv._id,
            returnNumber: returnInv.returnNumber || returnInv.invoiceNumber,
            deliveryInvoiceId: returnInv.deliveryInvoiceId,
            deliveryInvoiceIdType: typeof returnInv.deliveryInvoiceId,
            deliveryInvoiceIdValue: returnInv.deliveryInvoiceId
          });
        });
        
      } catch (returnError: any) {
        // Don't fail the main fetch if return invoices fail
        console.error('❌ Failed to fetch return invoices:', returnError);
        console.error('Return error details:', {
          message: returnError?.message,
          status: returnError?.response?.status,
          statusText: returnError?.response?.statusText,
          data: returnError?.response?.data
        });
      }
    } catch (error) {
      console.error('❌ Failed to fetch delivery invoices:', error);
      toast.error('Failed to fetch invoices');
    }
  };

  // Soft refresh: refetch delivery invoices when header clears cache
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateInvoicesCache();
      fetchInvoices();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  // Status update logic with permission checks
  const handleStatusUpdate = async (invoiceId: string, newStatus: 'sent' | 'completed') => {
    try {
      // Permission checks
      if (newStatus === 'sent') {
        // Anyone can update from processing to sent
        await apiService.patch(`/invoices/${invoiceId}`, { status: newStatus });
        toast.success(`Invoice marked as ${newStatus}`);
      } else if (newStatus === 'completed') {
        // Only admin can update from sent to completed
        if (!isAdmin) {
          toast.error('Only administrators can mark invoices as completed');
          return;
        }
        await apiService.patch(`/invoices/${invoiceId}`, { status: newStatus });
        toast.success(`Invoice marked as ${newStatus}`);
      }
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update invoice status');
    }
  };

  // Handler functions for form fields
  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c._id === customerId);
    setNewInvoice(prev => ({
      ...prev,
      customerId,
      customerAddress: customer?.address || '',
      customerPhone: customer?.phone || '',
      customerEmail: customer?.email || '',
    }));
  };

  const handleProjectChange = (projectId: string) => {
    const project = projects.find(p => p._id === projectId);
    setNewInvoice(prev => ({
      ...prev,
      projectId,
      projectAddress: project?.projectAddress || '',
      workOrderNumber: '',
      workOrderDate: '',
    }));
    setSelectedWorkOrder(null);
  };

  const handleWorkOrderChange = (workOrderNumber: string) => {
    const project = projects.find(p => p._id === newInvoice.projectId);
    const selectedWO = project?.workOrders?.find(wo => wo.number === workOrderNumber);
    
    if (selectedWO) {
      setSelectedWorkOrder(selectedWO);
      setNewInvoice(prev => ({
        ...prev,
        workOrderNumber: selectedWO.number,
        workOrderDate: selectedWO.date ? selectedWO.date.slice(0, 10) : '',
      }));
    } else {
      setSelectedWorkOrder(null);
      setNewInvoice(prev => ({
        ...prev,
        workOrderNumber: '',
        workOrderDate: '',
      }));
    }
  };

  const handleEmployeeChange = (employeeId: string) => {
    const employee = employees.find(e => e._id === employeeId);
    setNewInvoice(prev => ({
      ...prev,
      dispatchedThroughId: employeeId,
      dispatchedThroughPhone: employee?.phone || '',
    }));
  };


  const handleSubCategoryChange = (subCategoryId: string) => {
    setSelectedSubCategory(subCategoryId);
    setCurrentProduct(prev => ({ ...prev, productId: '' })); // Reset product selection
  };

  // Filter products based on selected sub category
  const getFilteredProducts = () => {
    if (!selectedSubCategory) {
      return products;
    }
    const filtered = products.filter(product => {
      // Check if product has a category and if it matches the selected sub category
      const matches = product.category?._id === selectedSubCategory;
      return matches;
    });
    return filtered;
  };

  // Get filtered products for searchable dropdown with flexible search
  const getSearchableProducts = () => {
    let filtered = getFilteredProducts();
    
    if (productSearchTerm.trim()) {
      const searchTerms = productSearchTerm.toLowerCase().trim().split(/\s+/);
      
      filtered = filtered.filter(product => {
        // Combine all searchable fields into one searchable text
        const searchableText = [
          product.name,
          product.barcode || '',
          product.sku || '',
          product.customFields?.brand || '',
          product.customFields?.model || '',
          product.customFields?.type || '',
          product.customFields?.compressorType || '',
          product.customFields?.countryOfOrigin || '',
          product.customFields?.countryOfAssembly || '',
          product.description || ''
        ].join(' ').toLowerCase();
        
        // Check if ALL search terms are found in the searchable text (in any order)
        return searchTerms.every(term => searchableText.includes(term));
      });
      
      // Sort by relevance - products with more matches and earlier matches rank higher
      filtered.sort((a, b) => {
        const aText = [
          a.name,
          a.barcode || '',
          a.sku || '',
          a.customFields?.brand || '',
          a.customFields?.model || ''
        ].join(' ').toLowerCase();
        
        const bText = [
          b.name,
          b.barcode || '',
          b.sku || '',
          b.customFields?.brand || '',
          b.customFields?.model || ''
        ].join(' ').toLowerCase();
        
        // Count how many search terms match in each product
        const aMatches = searchTerms.filter(term => aText.includes(term)).length;
        const bMatches = searchTerms.filter(term => bText.includes(term)).length;
        
        // If same number of matches, prioritize products where name starts with search term
        if (aMatches === bMatches) {
          const aNameStartsWith = searchTerms.some(term => a.name.toLowerCase().startsWith(term));
          const bNameStartsWith = searchTerms.some(term => b.name.toLowerCase().startsWith(term));
          
          if (aNameStartsWith && !bNameStartsWith) return -1;
          if (!aNameStartsWith && bNameStartsWith) return 1;
        }
        
        return bMatches - aMatches;
      });
    }
    
    return filtered;
  };

  const updateDropdownPosition = () => {
    if (productInputRef.current) {
      const rect = productInputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownMaxHeight = 240;
      const showAbove = spaceBelow < 180 && spaceAbove > spaceBelow;

      setDropdownPosition({
        top: showAbove ? Math.max(10, rect.top - Math.min(dropdownMaxHeight, spaceAbove - 10)) : rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 240)
      });
    }
  };

  // Handle product search
  const handleProductSearch = (searchTerm: string) => {
    setProductSearchTerm(searchTerm);
    updateDropdownPosition();
    setShowProductDropdown(true);
    
    // If search term is cleared, also clear the product selection
    if (!searchTerm.trim()) {
      setCurrentProduct(prev => ({
        ...prev,
        productId: '',
        name: '',
        unit: '',
        barcode: '',
        brand: '',
        model: '',
        sku: '',
        type: '',
        compressorType: '',
        countryOfOrigin: '',
        countryOfAssembly: '',
        details: '',
        description: '',
        capacity: '',
        serialNo: '',
        partNo: '',
      }));
    }
  };

  // Handle product selection from searchable dropdown
  const handleProductSelect = (product: any) => {
    setCurrentProduct(prev => ({
      ...prev,
      productId: product._id,
      name: product.name,
      unit: (product.units?.length ? product.units[0] : product.unit) || '',
      barcode: product.barcode || '',
      brand: product.customFields?.brand || '',
      model: product.customFields?.model || '',
      sku: product.sku || '',
      type: product.customFields?.type || '',
      compressorType: product.customFields?.compressorType || '',
      countryOfOrigin: product.customFields?.countryOfOrigin || '',
      countryOfAssembly: product.customFields?.countryOfAssembly || '',
      details: product.customFields?.details || '',
      description: product.description || '',
      capacity: product.customFields?.capacity || '',
      partNo: product.customFields?.partNo || '',
    }));
    setProductSearchTerm(product.name);
    setShowProductDropdown(false);
  };

  // Keep dropdown aligned on scroll or resize
  useEffect(() => {
    if (!showProductDropdown) return;
    updateDropdownPosition();

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showProductDropdown]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both the input ref and any dropdown items
      if (
        productDropdownRef.current && 
        !productDropdownRef.current.contains(target) &&
        !(target as Element).closest('.product-dropdown-item')
      ) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Product handling functions
  const handleCurrentProductChange = (field: keyof InvoiceProduct, value: any) => {
    setCurrentProduct(prev => {
      const isUnitField = field === 'unit';
      let numericValue = value;
      if (!isUnitField && typeof value === 'string' && value === '') {
        numericValue = 0;
      } else if (!isUnitField && typeof value === 'string') {
        numericValue = parseFloat(value) || 0;
      }
      let updated = { ...prev, [field]: isUnitField ? value : numericValue };
      
      // If productId or barcode changes, autofill from products
      if ((field === 'barcode' || field === 'productId') && value) {
        const found = products.find(p => (field === 'barcode' ? p.barcode === value : p._id === value));
        if (found) {
          updated = {
            ...updated,
            productId: found._id,
            barcode: found.barcode || '',
            name: found.name,
            unit: (found.units?.length ? found.units[0] : found.unit) || '',
            price: found.price || 0,
            // Copy all product details
            brand: found.customFields?.brand || '',
            model: found.customFields?.model || '',
            sku: found.sku || found.customFields?.sku || '',
            type: found.customFields?.type || '',
            compressorType: found.customFields?.compressorType || '',
            countryOfOrigin: found.customFields?.countryOfOrigin || '',
            countryOfAssembly: found.customFields?.countryOfAssembly || '',
            details: found.customFields?.details || '',
            description: found.description || found.customFields?.description || '',
            capacity: found.customFields?.capacity || '',
            partNo: found.customFields?.partNo || '',
            specification: found.customFields?.specification || (typeof found.specifications === 'string' ? found.specifications : '') || '',
          };
        }
      }
      
      // Calculate amount and total
      updated.amount = (updated.qty ?? 1) * (updated.price ?? 0);
      updated.total = updated.amount - (updated.discount || 0);
      return updated;
    });
  };

  // Add Product Modal Functions
  // Define air conditioner categories for dynamic field detection
  const airconCategories = ['Air-Conditioner', 'VRF', 'Chiller', 'Split Type'];
  
  // Check parent category for conditional fields
  const selectedParentCategoryObj = rootCategories.find(c => c._id === selectedParentCategory);
  const isAirConditioner = selectedParentCategoryObj ? airconCategories.includes(selectedParentCategoryObj.name) : false;

  const fetchSubCategories = async (parentId: string) => {
    try {
      const response = await apiService.get(`/categories/subcategories/${parentId}`);
      setSubCategories(response.data || []);
    } catch (error) {
    }
  };

  const handleParentCategoryChange = async (parentCategoryId: string) => {
    setSelectedParentCategory(parentCategoryId);
    setSelectedSubCategory(''); // Reset sub category when parent changes
    
    if (parentCategoryId) {
      await fetchSubCategories(parentCategoryId);
    } else {
      setSubCategories([]);
    }
    
    // Reset new product category when parent changes
    setNewProduct(prev => ({ ...prev, category: '' }));
  };

  const updateBarcode = (updatedProduct: any) => {
    setNewProduct(updatedProduct);
    
    // Generate barcode if we have enough data
    if (updatedProduct.brand && updatedProduct.model && updatedProduct.type && updatedProduct.compressorType) {
      const barcode = `${updatedProduct.brand}-${updatedProduct.model}-${updatedProduct.type}-${updatedProduct.compressorType}`.replace(/\s+/g, '').toUpperCase();
      setNewProduct(prev => ({ ...prev, generatedBarcode: barcode }));
      
      // Generate barcode image
      if (barcodeCanvasRef.current) {
        try {
          const canvas = barcodeCanvasRef.current;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            JsBarcode(canvas, barcode, {
              format: "CODE128",
              width: 2,
              height: 100,
              displayValue: true
            });
            setBarcodeImage(canvas.toDataURL());
          }
        } catch (error) {
        }
      }
    } else {
      setNewProduct(prev => ({ ...prev, generatedBarcode: '', barcodeNo: '' }));
      setBarcodeImage(null);
    }
  };

  const downloadBarcodeImage = () => {
    if (barcodeImage) {
      const link = document.createElement('a');
      link.download = `${newProduct.generatedBarcode || 'barcode'}.png`;
      link.href = barcodeImage;
      link.click();
    }
  };

  const handleCreateEntity = (type: string) => {
    setCreateModalType(type);
    setCreateModalData({ name: '', description: '', website: '', code: '', region: '' });
    setShowCreateModal(true);
  };

  const handleCreateEntitySubmit = async () => {
    try {
      let endpoint = '';
      const data: any = { name: createModalData.name };
      
      switch (createModalType) {
        case 'location':
          endpoint = '/locations';
          data.description = createModalData.description;
          break;
        case 'category':
          endpoint = '/categories';
          data.description = createModalData.description;
          break;
        case 'brand':
          endpoint = '/brands';
          data.description = createModalData.description;
          data.website = createModalData.website;
          break;
        case 'type':
          endpoint = '/product-types';
          data.description = createModalData.description;
          break;
        case 'compressorType':
          endpoint = '/compressor-types';
          data.description = createModalData.description;
          break;
        case 'country':
          endpoint = '/countries';
          data.description = createModalData.description;
          data.code = createModalData.code;
          data.region = createModalData.region;
          break;
        case 'unit':
          endpoint = '/units';
          data.description = createModalData.description;
          break;
        default:
          return;
      }
      
      await apiService.post(endpoint, data);
      toast.success(`${createModalType.charAt(0).toUpperCase() + createModalType.slice(1)} created successfully`);
      setShowCreateModal(false);
      
      // Refresh the relevant data
      const refreshData = async () => {
        try {
          const [locationsRes, brandsRes, typesRes, compressorTypesRes, countriesRes, unitsRes, categoriesRes] = await Promise.all([
            apiService.get('/locations/all'),
            apiService.get('/brands/all'),
            apiService.get('/product-types/all'),
            apiService.get('/compressor-types/all'),
            apiService.get('/countries/all'),
            apiService.get('/units/all'),
            apiService.get('/categories/all'),
          ]);
          
          setLocations(locationsRes.data || []);
          setBrands(brandsRes.data || []);
          setTypes(typesRes.data || []);
          setCompressorTypes(compressorTypesRes.data || []);
          setCountries(countriesRes.data || []);
          setUnits(unitsRes.data || []);
          setCategories(categoriesRes.data || []);
        } catch (error) {
        }
      };
      
      refreshData();
    } catch (error) {
      toast.error(`Failed to create ${createModalType}`);
    }
  };

  const handleAddNewProduct = async () => {
    try {
      
      // Find brand, type, compressor type, and countries by _id to get their names for the backend
      const brandObj = brands.find(brand => brand._id === newProduct.brand);
      const typeObj = types.find(type => type._id === newProduct.type);
      const compressorTypeObj = compressorTypes.find(comp => comp._id === newProduct.compressorType);
      const countryOfOriginObj = countries.find(country => country._id === newProduct.countryOfOrigin);
      const countryOfAssemblyObj = countries.find(country => country._id === newProduct.countryOfAssembly);
      
      
      const selectedUnitIds = newProduct.unitIds?.length ? newProduct.unitIds : (newProduct.unitId ? [newProduct.unitId] : []);
      const unitNames = selectedUnitIds.map((id: string) => units.find(u => u._id === id)?.name).filter(Boolean) as string[];
      const firstUnitName = unitNames[0] || (units.find(u => u._id === newProduct.unitId)?.name) || 'pcs';

      if (!newProduct.productName || !newProduct.productName.trim()) {
        toast.error('Product name is required');
        return;
      }
      if (!selectedUnitIds.length && !newProduct.unitId) {
        toast.error('Select at least one unit');
        return;
      }
      if (unitNames.length === 0 && !units.find(u => u._id === newProduct.unitId)) {
        toast.error('Selected unit is not valid');
        return;
      }

      const productData: any = {
        name: newProduct.productName,
        description: newProduct.description,
        unit: firstUnitName,
        ...(unitNames.length > 0 && { units: unitNames }),
        price: newProduct.price ? parseFloat(newProduct.price) : 0,
        costPrice: newProduct.costPrice ? parseFloat(newProduct.costPrice) : 0,
        stockQuantity: newProduct.stockQuantity ? parseInt(newProduct.stockQuantity) : 0,
        minStockLevel: newProduct.minStockLevel ? Math.max(0, parseInt(newProduct.minStockLevel)) : 0, // Ensure non-negative
        barcode: newProduct.barcodeNo,
        sku: newProduct.sku,
        status: newProduct.status,
        // Add custom fields for air conditioner specific data
        customFields: {
          model: newProduct.model,
          brand: brandObj ? brandObj.name : '',
          countryOfOrigin: countryOfOriginObj ? countryOfOriginObj.name : '',
          countryOfAssembly: countryOfAssemblyObj ? countryOfAssemblyObj.name : '',
          type: typeObj ? typeObj.name : '',
          compressorType: compressorTypeObj ? compressorTypeObj.name : '',
          specification: newProduct.specification,
          details: newProduct.details,
          serialNo: newProduct.serialNo,
          partNo: newProduct.partNo,
          generatedBarcode: newProduct.generatedBarcode,
        }
      };

      // Handle category logic
      if (newProduct.category && newProduct.category.trim() !== '') {
        // If a sub-category is selected, use it
        productData.category = newProduct.category;
      } else if (selectedParentCategory && subCategories.length === 0) {
        // If parent category is selected but no sub-categories available, use parent category
        productData.category = selectedParentCategory;
      }
      
      if (newProduct.location && newProduct.location.trim() !== '') {
        productData.location = newProduct.location;
      }

      // Remove empty string fields to avoid validation errors
      Object.keys(productData).forEach(key => {
        if (productData[key] === "" || productData[key] === null || productData[key] === undefined) {
          delete productData[key];
        }
      });

      // Clean up customFields to remove empty values
      if (productData.customFields) {
        Object.keys(productData.customFields).forEach(key => {
          if (productData.customFields[key] === "" || productData.customFields[key] === null || productData.customFields[key] === undefined) {
            delete productData.customFields[key];
          }
        });
      }

      // Check for duplicate product by name and barcode
      const duplicateByName = products.find(p => 
        p.name.toLowerCase().trim() === newProduct.productName.toLowerCase().trim()
      );
      
      const duplicateByBarcode = products.find(p => 
        p.barcode && newProduct.barcodeNo && 
        p.barcode.toLowerCase().trim() === newProduct.barcodeNo.toLowerCase().trim()
      );

      if (duplicateByName) {
        toast.error('Product already exists with the same name');
        return;
      }

      if (duplicateByBarcode) {
        toast.error('Product already exists with the same barcode');
        return;
      }


      const response = await apiService.post('/products', productData);
      
      toast.success('Product added successfully');
      
      // Immediately clear products cache and force refresh
      apiService.invalidateProductsCache();
      
      setShowAddProductModal(false);
      setNewProduct({
        productName: '',
        model: '',
        brand: '',
        countryOfOrigin: '',
        countryOfAssembly: '',
        type: '',
        compressorType: '',
        specification: '',
        details: '',
        serialNo: '',
        partNo: '',
        barcodeNo: '',
        generatedBarcode: '',
        sku: '',
        price: '',
        costPrice: '',
        stockQuantity: '',
        minStockLevel: '',
        unitId: '',
        unitIds: [],
        description: '',
        image: '',
        category: '',
        location: '',
        status: 'active',
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        const productsRes = await apiService.get('/products?limit=1000');
        setProducts(productsRes.data.products || []);
      }, 100);
    } catch (error: any) {
      
      if (error.response?.data?.errors) {
        // Display validation errors
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        // Display single error message
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to add product');
      }
    }
  };

  // Handler to add current product to the list
  const handleAddProduct = () => {
    // Validate
    if (!currentProduct.productId) {
      toast.error('Select a product');
      return;
    }
    if (addedProducts.some(p => p.productId === currentProduct.productId)) {
      toast.error('Product already added');
      return;
    }
    if ((currentProduct.qty ?? 1) <= 0) {
      toast.error('Quantity must be positive');
      return;
    }
    const selectedProduct = products.find(p => p._id === currentProduct.productId);
    if (!selectedProduct) return;
    const qty = Number(currentProduct.qty) || 0;
    // Check stock
    if ((selectedProduct.stockQuantity ?? 0) <= 0 || (selectedProduct.stockQuantity ?? 0) - qty < 0) {
      setInsufficientStockWarning('Insufficient Stock');
      return;
    }
    setInsufficientStockWarning(null);
    setAddedProducts(prev => [...prev, currentProduct]);
    setCurrentProduct({
      productId: '',
      barcode: '',
      name: '',
      unit: '',
      qty: 1,
      price: 0,
      amount: 0,
      discount: 0,
      total: 0,
      remarks: '',
      serialNumbers: [],
      warrantyDetails: {
        hasWarranty: false,
        serviceWarranty: null,
        sparePartsWarranty: null,
        compressorWarranty: null,
        warranty: null
      }
    });
    setFocusedFields(new Set());
  };

  // Handler to add 20 blank rows to the invoice
  const handleAddBlankRows = () => {
    const blankRows: InvoiceProduct[] = Array.from({ length: 20 }, () => ({
      productId: '',
      barcode: '',
      name: '',
      unit: '',
      qty: 0,
      price: 0,
      amount: 0,
      discount: 0,
      total: 0,
    }));
    setAddedProducts(prev => [...prev, ...blankRows]);
    setNewInvoice(prev => ({ ...prev, date: '' }));
    toast.success('20 blank rows added to the invoice');
  };

  // Handler to remove a product from the list
  const handleRemoveProduct = (idx: number) => {
    setAddedProducts(prev => prev.filter((_, i) => i !== idx));
  };

  // Load Added Products from a selected Purchase Invoice (Add Delivery Invoice only)
  const handleLoadFromPurchaseInvoice = (invoiceId: string) => {
    setSelectedPurchaseInvoiceId(invoiceId);
    if (!invoiceId) {
      setAddedProducts([]);
      return;
    }
    const inv = purchaseInvoicesForLoad.find((p: PurchaseInvoiceOption) => p._id === invoiceId);
    if (!inv || !inv.items || inv.items.length === 0) {
      setAddedProducts([]);
      if (invoiceId) toast.error('Purchase invoice has no items');
      return;
    }
    const mapped: InvoiceProduct[] = inv.items.map((item: PurchaseInvoiceOption['items'][0]) => {
      const productId = typeof item.product === 'object' && item.product !== null
        ? (item.product as any)._id
        : (item.productId || item.product || '');
      const qty = Number(item.qty ?? item.quantity) || 0;
      const unitPrice = Number(item.purchaseRate ?? item.unitPrice) || 0;
      const discount = Number(item.discount) || 0;
      const amount = qty * unitPrice;
      const total = (item.totalPrice != null ? Number(item.totalPrice) : amount - discount);
      return {
        productId: productId || '',
        barcode: item.barcode || '',
        name: item.name || '',
        unit: item.unit || '',
        qty,
        price: unitPrice,
        amount,
        discount,
        total,
        brand: item.brand,
        model: item.model,
        sku: item.sku,
        type: item.type,
        compressorType: item.compressorType,
        countryOfOrigin: item.countryOfOrigin,
        countryOfAssembly: item.countryOfAssembly,
        details: item.details,
        description: item.description,
        capacity: item.capacity,
        serialNo: item.serialNo,
        serialNumbers: (item as any).serialNumbers || [],
        partNo: item.partNo,
        remarks: item.remarks || '',
        warrantyDetails: (item as any).warrantyDetails || {
          hasWarranty: false,
          serviceWarranty: null,
          sparePartsWarranty: null,
          compressorWarranty: null,
          warranty: null
        },
      };
    });
    setAddedProducts(mapped);
    toast.success(`Loaded ${mapped.length} product(s) from purchase invoice`);
  };

  // Handler to toggle row selection
  const handleSelectRow = (idx: number) => {
    setSelectedRows(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  // Handler to select all
  const handleSelectAll = () => {
    if (selectedRows.length === addedProducts.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(addedProducts.map((_, idx) => idx));
    }
  };

  // Handler for bulk actions
  const handleBulkDelete = () => {
    if (!window.confirm('Are you sure you want to delete the selected products?')) return;
    setAddedProducts(prev => prev.filter((_, idx) => !selectedRows.includes(idx)));
    setSelectedRows([]);
  };



  // Enhanced Save with Validation and Spinner
  const handleSaveInvoice = async () => {
    // Clear previous errors
    setFormErrors({ customer: '', project: '', date: '', products: [] });
    
    // Validate form - only Customer, Project, Date, and at least 1 product are required
    const errors: { customer?: string; project?: string; date?: string; products: string[] } = { customer: '', project: '', date: '', products: [] };
    if (!newInvoice.customerId) {
      errors.customer = 'Customer is required';
    }
    if (!newInvoice.projectId) {
      errors.project = 'Project is required';
    }
    const allItemsBlank = addedProducts.length > 0 && addedProducts.every(p => !p.productId || p.productId.trim() === '');
    if (!allItemsBlank && !newInvoice.date) {
      errors.date = 'Date is required';
    }
    // Check if we have at least one item (blank or non-blank)
    if (addedProducts.length === 0) {
      errors.products.push('At least one item is required');
    }
    setFormErrors(errors);
    const hasError = errors.customer || errors.project || errors.date || errors.products.length > 0;
    if (hasError) {
      toast.error('Please fix the errors in the form');
      return;
    }
    setSaving(true);
    try {
      // Prepare payload for backend - include all products (blank and non-blank)
      const items: any[] = addedProducts.map(p => ({
        product: p.productId || null,
        quantity: p.qty || 0,
        unit: p.unit || '',
        unitPrice: p.price || 0,
        discount: p.discount || 0,
        totalPrice: p.total || 0,
        name: p.name || '',
        brand: p.brand || '',
        model: p.model || '',
        sku: p.sku || '',
        type: p.type || '',
        compressorType: p.compressorType || '',
        countryOfOrigin: p.countryOfOrigin || '',
        countryOfAssembly: p.countryOfAssembly || '',
        details: p.details || '',
        description: p.description || '',
        capacity: p.capacity || '',
        serialNo: '',
        serialNumbers: p.serialNumbers || [],
        partNo: p.partNo || '',
        remarks: p.remarks || '',
        warrantyDetails: p.warrantyDetails
      }));
      
      // Check if all items are blank (no productId)
      // allItemsBlank is already calculated above
      
      const payload: any = {
        type: 'delivery',
        date: newInvoice.date || undefined,
        project: newInvoice.projectId || undefined,
        customer: {
          _id: newInvoice.customerId,
          name: customers.find(c => c._id === newInvoice.customerId)?.name || '',
          address: newInvoice.customerAddress,
          phone: newInvoice.customerPhone,
          email: newInvoice.customerEmail,
        },
        deliveryAddress: newInvoice.deliveryAddress,
        workOrderNumber: newInvoice.workOrderNumber,
        workOrderDate: newInvoice.workOrderDate,
        dispatchedThrough: {
          name: employees.find(e => e._id === newInvoice.dispatchedThroughId)?.name || '',
          phone: newInvoice.dispatchedThroughPhone,
        },
        items,
        subtotal: addedProducts.reduce((sum, p) => sum + (p.total || 0), 0),
        discount: 0, // Item-level discounts are already included in subtotal
        total: addedProducts.reduce((sum, p) => sum + (p.total || 0), 0),
        status: allItemsBlank ? 'blank' : 'processing',
        preparedBy: user?._id,
      };
      
      console.log('💾 Saving invoice with payload:', JSON.stringify(payload, null, 2));
      console.log('📦 Items count:', items.length);
      console.log('📋 First item sample:', items[0]);
      
      if (showEditModal && editingInvoice) {
        if (editingInvoice.status === 'paid') {
          toast.error('Cannot edit a paid invoice');
          setSaving(false);
          return;
        }
        const response = await apiService.put(`/invoices/${editingInvoice._id}`, payload);
        toast.success('Invoice updated');
      } else {
        const response = await apiService.post('/invoices', payload, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
        toast.success('Invoice created');
      }
      
      // Immediately clear invoices cache and force refresh
      apiService.invalidateInvoicesCache();
      
      setShowAddModal(false);
      setShowEditModal(false);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchInvoices();
      }, 100);
      setAddedProducts([]);
      setCurrentProduct({
        productId: '',
        barcode: '',
        name: '',
        unit: '',
        qty: 1,
        price: 0,
        amount: 0,
        discount: 0,
        total: 0,
        remarks: '',
        warrantyDetails: {
          hasWarranty: false,
          serviceWarranty: null,
          sparePartsWarranty: null,
          compressorWarranty: null,
          warranty: null
        }
      });
      setFormErrors({ customer: '', project: '', date: '', products: [] });
      setNewInvoice({
        invoiceNumber: '',
        date: getTodayDateString(),
        customerId: '',
        customerAddress: '',
        customerPhone: '',
        customerEmail: '',
        projectId: '',
        projectAddress: '',
        workOrderNumber: '',
        workOrderDate: '',
        dispatchedThroughId: '',
        dispatchedThroughPhone: '',
        deliveryAddress: '',
        products: [
          {
            productId: '',
            barcode: '',
            name: '',
            unit: '',
            qty: 1,
            price: 0,
                    amount: 0,
                    discount: 0,
                    total: 0,
                    remarks: '',
                    warrantyDetails: {              hasWarranty: false,
              serviceWarranty: null,
              sparePartsWarranty: null,
              compressorWarranty: null,
              warranty: null
            }
          },
        ],
      });
    } catch (error: any) {
      console.error('Invoice save error:', error.response?.data);
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Display validation errors
        const errorMessages = error.response.data.errors.map((err: any) => err.msg || err.message).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to save invoice');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateInvoice = async (invoice: DeliveryInvoice) => {
    try {
      const nextInvoiceNumber = await fetchNextDeliveryInvoiceNumber().catch(() => 'DINV-0001001');
      const isBlank = invoice.status === 'blank' || isInvoiceBlank(invoice);
      
      setNewInvoice({
        invoiceNumber: nextInvoiceNumber,
        date: isBlank ? '' : getTodayDateString(),
        customerId: customers.find(c => c.name === invoice.customer?.name)?._id || '',
        customerAddress: invoice.customer?.address || '',
        customerPhone: invoice.customer?.phone || '',
        customerEmail: invoice.customer?.email || '',
        projectId: invoice.project?._id || '',
        projectAddress: projects.find(p => p._id === invoice.project?._id)?.projectAddress || '',
        workOrderNumber: invoice.workOrder?.number || '',
        workOrderDate: invoice.workOrder?.date ? invoice.workOrder.date.slice(0, 10) : '',
        dispatchedThroughId: invoice.dispatchedThrough?.name ? (employees.find(e => e.name === invoice.dispatchedThrough?.name)?._id || '') : '',
        dispatchedThroughPhone: invoice.dispatchedThrough?.phone || '',
        deliveryAddress: invoice.deliveryAddress || '',
        products: (invoice.items || []).map(item => {
          const itemProductId = (item as any).productId ?? ((item as any).product && (item as any).product._id) ?? '';
          const itemBarcode = (item as any).barcode ?? ((item as any).product && (item as any).product.barcode) ?? '';
          const itemName = (item as any).name ?? ((item as any).product && (item as any).product.name) ?? '';
          const itemUnit = (item as any).unit ?? ((item as any).product && (item as any).product.unit) ?? '';
          const qty = (item as any).qty ?? (item as any).quantity ?? 1;
          const price = (item as any).price ?? (item as any).unitPrice ?? 0;
          const discount = (item as any).discount ?? 0;
          return {
            productId: itemProductId,
            barcode: itemBarcode,
            name: itemName,
            unit: itemUnit,
            qty,
            price,
            amount: qty * price,
            discount,
            total: qty * price - discount,
            brand: (item as any).brand ?? '',
            model: (item as any).model ?? '',
            sku: (item as any).sku ?? '',
            type: (item as any).type ?? '',
            compressorType: (item as any).compressorType ?? '',
            countryOfOrigin: (item as any).countryOfOrigin ?? '',
            countryOfAssembly: (item as any).countryOfAssembly ?? '',
            details: (item as any).details ?? '',
            description: (item as any).description ?? '',
            capacity: (item as any).capacity ?? '',
            serialNo: (item as any).serialNo ?? '',
            partNo: (item as any).partNo ?? '',
            remarks: (item as any).remarks ?? '',
            warrantyDetails: (item as any).warrantyDetails ?? {
              hasWarranty: false,
              serviceWarranty: null,
              sparePartsWarranty: null,
              compressorWarranty: null,
              warranty: null
            },
          };
        }),
      });
      
      const mappedProducts = (invoice.items || []).map(item => {
        const itemProductId = (item as any).productId ?? ((item as any).product && (item as any).product._id) ?? '';
        const itemBarcode = (item as any).barcode ?? ((item as any).product && (item as any).product.barcode) ?? '';
        const itemName = (item as any).name ?? ((item as any).product && (item as any).product.name) ?? '';
        const itemUnit = (item as any).unit ?? ((item as any).product && (item as any).product.unit) ?? '';
        const qty = (item as any).qty ?? (item as any).quantity ?? 1;
        const price = (item as any).price ?? (item as any).unitPrice ?? 0;
        const discount = (item as any).discount ?? 0;
        return {
          productId: itemProductId,
          barcode: itemBarcode,
          name: itemName,
          unit: itemUnit,
          qty,
          price,
          amount: qty * price,
          discount,
          total: qty * price - discount,
          brand: (item as any).brand ?? '',
          model: (item as any).model ?? '',
          sku: (item as any).sku ?? '',
          type: (item as any).type ?? '',
          compressorType: (item as any).compressorType ?? '',
          countryOfOrigin: (item as any).countryOfOrigin ?? '',
          countryOfAssembly: (item as any).countryOfAssembly ?? '',
          details: (item as any).details ?? '',
          description: (item as any).description ?? '',
          capacity: (item as any).capacity ?? '',
          serialNo: (item as any).serialNo ?? '',
          partNo: (item as any).partNo ?? '',
          remarks: (item as any).remarks ?? '',
          warrantyDetails: (item as any).warrantyDetails ?? {
            hasWarranty: false,
            serviceWarranty: null,
            sparePartsWarranty: null,
            compressorWarranty: null,
            warranty: null
          },
        };
      });
      setAddedProducts(mappedProducts);
      
      setEditingInvoice(null);
      setShowEditModal(false);
      setShowAddModal(true);
      toast.success('Invoice duplicated. You can review and save.');
    } catch (err) {
      toast.error('Failed to duplicate invoice.');
    }
  };

  const handlePrintMultipleInvoices = () => {
    const selectedInvoices = invoices.filter(inv => selectedInvoiceIds.includes(inv._id));
    if (selectedInvoices.length === 0) return;
    
    setSelectedInvoicesForPrint(selectedInvoices);
    setShowMultiplePrintPreview(true);
  };

  // Print functionality now handled by PrintPreviewModal

  useEffect(() => {
    if (showEditModal && editingInvoice && products.length > 0) {
      setAddedProducts((editingInvoice.items || []).map(item => {
        // Defensive property access for DeliveryInvoice items
        const itemProductId = (item as any).productId ?? (item as any).product?._id;
        const itemBarcode = (item as any).barcode ?? (item as any).product?.barcode;
        const itemName = (item as any).name ?? (item as any).product?.name;
        const qty = (item as any).qty ?? (item as any).quantity ?? 1;
        const price = (item as any).price ?? (item as any).unitPrice ?? 0;
        const discount = (item as any).discount ?? 0;
        const safeQty = qty ?? 1;
        const safePrice = price ?? 0;
        let found = null;
        if (itemProductId) {
          found = products.find(p => p._id === itemProductId);
        }
        if (!found && itemBarcode) {
          found = products.find(p => p.barcode === itemBarcode);
        }
        if (!found && itemName) {
          found = products.find(p => p.name && p.name.toLowerCase() === itemName.toLowerCase());
        }
        return found
          ? {
              productId: found._id,
              barcode: found.barcode || itemBarcode || '',
              name: found.name || itemName || '',
              unit: found.unit || (item as any).unit || (item as any).product?.unit || '',
              qty: safeQty,
              price: safePrice,
              amount: safeQty * safePrice,
              discount: (item as any).discount || 0,
              total: safeQty * safePrice - ((item as any).discount || 0),
              brand: (item as any).brand || found.customFields?.brand || '',
              model: (item as any).model || found.customFields?.model || '',
              sku: (item as any).sku || found.sku || found.customFields?.sku || '',
              type: (item as any).type || found.customFields?.type || '',
              compressorType: (item as any).compressorType || found.customFields?.compressorType || '',
              countryOfOrigin: (item as any).countryOfOrigin || found.customFields?.countryOfOrigin || '',
              countryOfAssembly: (item as any).countryOfAssembly || found.customFields?.countryOfAssembly || '',
              details: (item as any).details || found.customFields?.details || '',
              description: (item as any).description || found.description || found.customFields?.description || '',
              capacity: (item as any).capacity || found.customFields?.capacity || '',
              serialNo: (item as any).serialNo || found.customFields?.serialNo || '',
              serialNumbers: (item as any).serialNumbers || [],
              partNo: (item as any).partNo || found.customFields?.partNo || '',
              remarks: (item as any).remarks || '',
              warrantyDetails: (item as any).warrantyDetails || {
                hasWarranty: false,
                serviceWarranty: null,
                sparePartsWarranty: null,
                compressorWarranty: null,
                warranty: null
              }
            }
          : {
              productId: itemProductId || '',
              barcode: itemBarcode || '',
              name: itemName || '',
              unit: (item as any).unit || (item as any).product?.unit || '',
              qty: safeQty,
              price: safePrice,
              amount: safeQty * safePrice,
              discount: (item as any).discount || 0,
              total: safeQty * safePrice - ((item as any).discount || 0),
              brand: (item as any).brand || '',
              model: (item as any).model || '',
              sku: (item as any).sku || '',
              type: (item as any).type || '',
              compressorType: (item as any).compressorType || '',
              countryOfOrigin: (item as any).countryOfOrigin || '',
              countryOfAssembly: (item as any).countryOfAssembly || '',
              details: (item as any).details || '',
              description: (item as any).description || '',
              capacity: (item as any).capacity || '',
              serialNo: (item as any).serialNo || '',
              serialNumbers: (item as any).serialNumbers || [],
              partNo: (item as any).partNo || '',
              remarks: (item as any).remarks || '',
              warrantyDetails: (item as any).warrantyDetails || {
                hasWarranty: false,
                serviceWarranty: null,
                sparePartsWarranty: null,
                compressorWarranty: null,
                warranty: null
              }
            };
      }));
    }
  }, [showEditModal, editingInvoice, products]);

  const fetchInvoiceWarranties = async (invoiceId: string) => {
    try {
      setLoadingWarranties(true);
      const response = await apiService.get(`/warranties/invoice/${invoiceId}`);
      setInvoiceWarranties(response.data || []);
    } catch (error) {
      console.error('Failed to fetch invoice warranties:', error);
      toast.error('Failed to load warranty details');
    } finally {
      setLoadingWarranties(false);
    }
  };

  const openWarrantyModal = (invoice: DeliveryInvoice) => {
    setSelectedInvoiceForWarranty(invoice);
    setShowWarrantyModal(true);
    fetchInvoiceWarranties(invoice._id);
  };

  // Function to check if a delivery invoice has any return invoices
  const hasReturnedItems = (invoice: DeliveryInvoice) => {
    // console.log(`Checking invoice ${invoice.invoiceNumber} (${invoice._id}) for returns:`, {
    //   totalReturnInvoices: returnInvoices.length,
    //   returnInvoices: returnInvoices.map(ri => ({
    //     id: ri._id,
    //     deliveryInvoiceId: ri.deliveryInvoiceId,
    //     deliveryInvoiceIdType: typeof ri.deliveryInvoiceId
    //   }))
    // });
    
    // Check if there are any return invoices that reference this delivery invoice
    const hasReturns = returnInvoices.some(returnInv => 
      returnInv.deliveryInvoiceId === invoice._id || 
      returnInv.deliveryInvoiceId?._id === invoice._id
    );
    
    // Debug logging
    // console.log(`Invoice ${invoice.invoiceNumber} has returns: ${hasReturns}`);
    
    // if (hasReturns) {
    //   console.log(`Invoice ${invoice.invoiceNumber} has return invoices:`, {
    //     invoiceId: invoice._id,
    //     returnInvoices: returnInvoices.filter(returnInv => 
    //       returnInv.deliveryInvoiceId === invoice._id || 
    //       returnInv.deliveryInvoiceId?._id === invoice._id
    //     )
    //   });
    // }
    
    return hasReturns;
  };

  // Handler to open Add Delivery Invoice modal
  const handleOpenAddModal = () => {
    setShowAddModal(true);
    setAddedProducts([]);
    setCurrentProduct({ 
      productId: '', 
      barcode: '', 
      name: '', 
      qty: 1, 
      price: 0, 
      discount: 0, 
      amount: 0, 
      total: 0,
      warrantyDetails: {
        hasWarranty: false,
        serviceWarranty: null,
        sparePartsWarranty: null,
        compressorWarranty: null,
        warranty: null
      }
    });
    setInsufficientStockWarning(null);
    setFormErrors({ customer: '', project: '', date: '', products: [] });
    // Reset any other relevant fields here
  };

  // Filter button handler
  const handleApplyFilters = () => {
    setFilterCustomer(pendingFilterCustomer);
    setFilterProject(pendingFilterProject);
    setFilterStartDate(pendingFilterStartDate);
    setFilterEndDate(pendingFilterEndDate);
    setFilterStatus(pendingFilterStatus);
  };

  // Reset button handler
  const handleResetFilters = () => {
    setPendingFilterCustomer('');
    setPendingFilterProject('');
    setPendingFilterStartDate('');
    setPendingFilterEndDate('');
    setPendingFilterStatus('');
    setFilterCustomer('');
    setFilterProject('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterStatus('');
  };

  // --- Add/Edit Delivery Invoice Modal Project Dropdown Filtering ---
  // For Add Modal
  const addModalSelectedCustomerId = newInvoice?.customerId || '';
  const addModalSelectedCustomerObj = customers.find(c => c._id === addModalSelectedCustomerId);
  const addModalSelectedCustomerName = addModalSelectedCustomerObj ? addModalSelectedCustomerObj.name : '';
  const addModalFilteredProjects = addModalSelectedCustomerName
    ? projects.filter(p => p.customerName === addModalSelectedCustomerName)
    : [];
  // For Edit Modal
  const editModalSelectedCustomerId = editingInvoice && ('customerId' in editingInvoice)
    ? (editingInvoice as any).customerId
    : (editingInvoice && editingInvoice.customer && '_id' in editingInvoice.customer ? (editingInvoice.customer as any)._id : '');
  const editModalSelectedCustomerObj = customers.find(c => c._id === editModalSelectedCustomerId);
  const editModalSelectedCustomerName = editModalSelectedCustomerObj ? editModalSelectedCustomerObj.name : '';
  const editModalFilteredProjects = editModalSelectedCustomerName
    ? projects.filter(p => p.customerName === editModalSelectedCustomerName)
    : [];

  // BarcodeScanner component using html5-qrcode (copied from PurchaseInvoicesPage)
  interface BarcodeScannerProps {
    onDetected: (barcode: string) => void;
    onClose: () => void;
  }
  const BarcodeScanner = React.forwardRef<any, BarcodeScannerProps>(({ onDetected, onClose }, ref) => {
    const html5QrRef = useRef<Html5Qrcode | null>(null);
    const scanHandled = useRef(false);
    const running = useRef(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scannerIdRef = useRef('');

    // Helper to stop and clear the camera
    const stopCamera = async () => {
      if (html5QrRef.current) {
        try { await html5QrRef.current.stop(); } catch { /* intentionally left empty */ }
        try { await html5QrRef.current.clear(); } catch { /* intentionally left empty */ }
      }
      // Force stop all video tracks from all video elements
      document.querySelectorAll('video').forEach(videoElem => {
        if (videoElem.srcObject) {
          const tracks = (videoElem.srcObject as MediaStream).getTracks();
          tracks.forEach(track => track.stop());
          videoElem.srcObject = null;
        }
      });
    };

    React.useImperativeHandle(ref, () => ({ stopCamera }));

    useEffect(() => {
      const scannerDiv = document.createElement('div');
      const scannerId = `barcode-scanner-video-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      scannerIdRef.current = scannerId;
      scannerDiv.id = scannerId;
      scannerDiv.style.width = '400px';
      scannerDiv.style.maxWidth = '100%';
      if (containerRef.current) {
        containerRef.current.appendChild(scannerDiv);
      }
      html5QrRef.current = new Html5Qrcode(scannerId);
      scanHandled.current = false;
      running.current = false;
      html5QrRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          if (!scanHandled.current) {
            scanHandled.current = true;
            running.current = false;
            stopCamera().then(() => {
              onDetected(decodedText);
            }).catch(() => {
              onDetected(decodedText);
            });
          }
        },
        () => { /* intentionally left empty */ }
      ).then(() => {
        running.current = true;
      }).catch(() => {
        running.current = false;
      });
      return () => {
        stopCamera();
        if (containerRef.current && scannerDiv.parentNode === containerRef.current) {
          containerRef.current.removeChild(scannerDiv);
        }
      };
    }, []);

    const handleClose = async () => {
      await stopCamera();
      onClose();
    };

    return (
      <div>
        <div ref={containerRef} />
        <button className="mt-4 px-4 py-2 bg-gray-300 rounded" onClick={handleClose}>
          Cancel
        </button>
      </div>
    );
  });

  // Helper function to handle focus behavior for input fields
  const handleInputFocus = (field: keyof InvoiceProduct) => {
    setFocusedFields(prev => new Set(prev).add(field));
    
    // Clear the field if it's 0 and hasn't been focused before
    if (currentProduct[field] === 0 && !focusedFields.has(field)) {
      setCurrentProduct(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Helper function to get display value for input fields
  const getInputValue = (field: keyof InvoiceProduct) => {
    const value = currentProduct[field];
    if (value === 0 && !focusedFields.has(field)) {
      return '';
    }
    return value?.toString() || '';
  };

  // Helper function to render product details with all available fields
  const renderProductDetails = (prod: InvoiceProduct, selectedProduct: Product | undefined) => {
    const custom = selectedProduct?.customFields || {};
    
    // Get specifications as string if available - improved logic
    let specifications = '';
    if (selectedProduct && selectedProduct.specifications) {
      if (typeof selectedProduct.specifications === 'string') {
        specifications = selectedProduct.specifications;
      } else if (typeof selectedProduct.specifications === 'object' && Object.keys(selectedProduct.specifications).length > 0) {
        specifications = JSON.stringify(selectedProduct.specifications);
      }
    } else if (custom.specifications) {
      if (typeof custom.specifications === 'string') {
        specifications = custom.specifications;
      } else if (typeof custom.specifications === 'object' && Object.keys(custom.specifications).length > 0) {
        specifications = JSON.stringify(custom.specifications);
      }
    }
    
    // Serial No., Part No., and Specification from prod or custom
    const serialNo = prod.serialNo || custom.serialNo;
    const partNo = prod.partNo || custom.partNo;
    const specification = prod.specification || custom.specification;
    
    // Define all possible fields with their labels and values
    const fieldMap = [
      { key: 'name', label: 'Product Name', value: prod.name },
      { key: 'brand', label: 'Brand', value: custom.brand },
      { key: 'model', label: 'Model', value: custom.model },
      { key: 'sku', label: 'SKU', value: selectedProduct?.sku || custom.sku },
      { key: 'type', label: 'Type', value: custom.type },
      { key: 'compressorType', label: 'Compressor Type', value: custom.compressorType },
      { key: 'countryOfOrigin', label: 'Country of Origin', value: custom.countryOfOrigin },
      { key: 'countryOfAssembly', label: 'Country of Assembly', value: custom.countryOfAssembly },
      { key: 'details', label: 'Details', value: custom.details },
      { key: 'description', label: 'Description', value: selectedProduct?.description || custom.description },
      { key: 'capacity', label: 'Capacity', value: custom.capacity },
      { key: 'specifications', label: 'Specifications', value: specification || specifications },
      { key: 'partNo', label: 'Part No.', value: partNo },
      { key: 'remarks', label: 'Note', value: prod.remarks },
    ];
    
    // Filter out empty values and render - improved filtering
    const descLines = fieldMap
      .filter(f => {
        if (!f.value) return false;
        if (typeof f.value === 'string') {
          const trimmed = f.value.trim();
          return trimmed !== '' && trimmed !== '-' && trimmed !== 'undefined' && trimmed !== 'null';
        }
        if (typeof f.value === 'object') {
          return Object.keys(f.value).length > 0;
        }
        return true; // Allow other non-string values like numbers
      })
      .map(f => <div key={f.key}><span className="font-semibold">{f.label}:</span> {f.value}</div>);

    // Add Warranty details to the display
    if (prod.warrantyDetails?.hasWarranty) {
      const formatWarranty = (years: number) => `${years} ${years === 1 ? 'Year' : 'Years'}`;
      const warranties = [];
      if (prod.warrantyDetails.serviceWarranty !== null && prod.warrantyDetails.serviceWarranty !== undefined) {
        warranties.push(`Service: ${formatWarranty(prod.warrantyDetails.serviceWarranty)}`);
      }
      if (prod.warrantyDetails.sparePartsWarranty !== null && prod.warrantyDetails.sparePartsWarranty !== undefined) {
        warranties.push(`Spare Parts: ${formatWarranty(prod.warrantyDetails.sparePartsWarranty)}`);
      }
      if (prod.warrantyDetails.compressorWarranty !== null && prod.warrantyDetails.compressorWarranty !== undefined) {
        warranties.push(`Compressor: ${formatWarranty(prod.warrantyDetails.compressorWarranty)}`);
      }
      if (prod.warrantyDetails.warranty !== null && prod.warrantyDetails.warranty !== undefined) {
        warranties.push(`General: ${formatWarranty(prod.warrantyDetails.warranty)}`);
      }

      if (warranties.length > 0) {
        descLines.push(
          <div key="warranty-info" className="mt-1 p-1 bg-indigo-50 border border-indigo-100 rounded text-[10px]">
            <span className="font-bold text-indigo-700">Warranty:</span> {warranties.join(', ')}
          </div>
        );
      }
    }
    
    return descLines.length > 0 ? descLines : <div>-</div>;
  };



  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-4">Delivery Invoices</h1>
      {loading ? <LoadingSpinner size="lg" /> : (
        <>
          {/* Header with actions */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-text)]">
                Manage Delivery Invoices
              </h2>
              <p className="text-[var(--color-text-secondary)]">
                Create and manage delivery invoices for customers
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { 
                  apiService.invalidateInvoicesCache(); 
                  fetchInvoices();
                  toast.success('Data refreshed successfully');
                }}
                title="Refresh"
                className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={handleExportInvoices}
                className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
                style={{ fontFamily: 'var(--font-family)', borderRadius: 'var(--border-radius)' } as any}
              >
                <Download className="w-5 h-5" />
                <span>Export</span>
              </button>
              {selectedInvoiceIds.length > 0 && (
                <button
                  onClick={handlePrintMultipleInvoices}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-['Times_New_Roman']"
                  style={{ fontFamily: 'var(--font-family)', borderRadius: 'var(--border-radius)' } as any}
                >
                  <Printer className="w-5 h-5" />
                  <span>Print Selected ({selectedInvoiceIds.length})</span>
                </button>
              )}
              <Button
                variant="primary"
                onClick={() => setShowAddModal(true)}
                icon={<Plus className="w-5 h-5" />}
              >
                Add Delivery Invoice
              </Button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by customer name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
                >
                  <Filter className="w-4 h-4" />
                  <span>Filters</span>
                </button>
              </div>
            </div>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 } as any}
                animate={{ opacity: 1, height: 'auto' } as any}
                exit={{ opacity: 0, height: 0 } as any}
                className="mt-4 pt-4 border-t border-[var(--color-border)]"
              >
                <div className="flex flex-wrap gap-4 mb-4 items-end">
                  {/* Customer Filter */}
                  <div>
                    <label className="block text-xs font-medium mb-1">Customer</label>
                    <select
                      className="border rounded px-2 py-1 min-w-[180px]"
                      value={pendingFilterCustomer}
                      onChange={e => {
                        setPendingFilterCustomer(e.target.value);
                        setPendingFilterProject(''); // Reset project when customer changes
                      }}
                    >
                      <option value="">All</option>
                      {customers.map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Project Filter (only if customer is selected) */}
                  {pendingFilterCustomer && (
                    <div>
                      <label className="block text-xs font-medium mb-1">Project</label>
                      <select
                        className="border rounded px-2 py-1 min-w-[180px]"
                        value={pendingFilterProject}
                        onChange={e => setPendingFilterProject(e.target.value)}
                      >
                        <option value="">All</option>
                        {filteredProjects.map(p => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Time Period Filter */}
                  <div>
                    <label className="block text-xs font-medium mb-1">From</label>
                    <input
                      type="date"
                      className="border rounded px-2 py-1"
                      value={pendingFilterStartDate}
                      onChange={e => setPendingFilterStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">To</label>
                    <input
                      type="date"
                      className="border rounded px-2 py-1"
                      value={pendingFilterEndDate}
                      onChange={e => setPendingFilterEndDate(e.target.value)}
                    />
                  </div>
                  {/* Status Filter */}
                  <div>
                    <label className="block text-xs font-medium mb-1">Status</label>
                    <select
                      className="border rounded px-2 py-1"
                      value={pendingFilterStatus}
                      onChange={e => setPendingFilterStatus(e.target.value)}
                    >
                      <option value="">All</option>
                      {uniqueStatuses.map(status => (
                        <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  {/* Filter/Reset Buttons */}
                  <div className="flex gap-2">
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded"
                      onClick={handleApplyFilters}
                    >
                      Filter
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded"
                      onClick={handleResetFilters}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Invoices Table */}
          <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden mt-6" style={{ borderRadius: 'var(--border-radius)' } as any}>
            <DoubleScrollbar>
              <table className="min-w-full divide-y divide-[var(--color-border)]">
                <thead className="bg-[var(--color-surface-light)]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman'] w-10">
                      <input 
                        type="checkbox" 
                        className="form-checkbox h-4 w-4" 
                        checked={selectedInvoiceIds.length === searchFilteredInvoices.length && searchFilteredInvoices.length > 0} 
                        onChange={() => {
                          if (selectedInvoiceIds.length === searchFilteredInvoices.length) {
                            setSelectedInvoiceIds([]);
                          } else {
                            setSelectedInvoiceIds(searchFilteredInvoices.map(inv => inv._id));
                          }
                        }}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Total Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
                  {searchFilteredInvoices.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-[var(--color-text-secondary)]">
                        {invoices.length === 0 ? 'No delivery invoices found.' : 'No invoices match your filters.'}
                      </td>
                    </tr>
                  )}
                  {(searchFilteredInvoices || []).map((invoice) => (
                    <tr key={invoice._id} className="hover:bg-[var(--color-surface-light)]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input 
                          type="checkbox" 
                          className="form-checkbox h-4 w-4" 
                          checked={selectedInvoiceIds.includes(invoice._id)} 
                          onChange={() => {
                            if (selectedInvoiceIds.includes(invoice._id)) {
                              setSelectedInvoiceIds(prev => prev.filter(id => id !== invoice._id));
                            } else {
                              setSelectedInvoiceIds(prev => [...prev, invoice._id]);
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{invoice.invoiceNumber || invoice._id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{invoice.customer?.name || ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{invoice.project?.name || ''}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{invoice.date ? new Date(invoice.date).toLocaleDateString() : new Date(invoice.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{safeCurrency(invoice.total)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${getStatusColor(getEffectiveStatus(invoice))}`}>{getEffectiveStatus(invoice).charAt(0).toUpperCase() + getEffectiveStatus(invoice).slice(1)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            className="text-blue-600 hover:text-blue-800 font-['Times_New_Roman']"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {(invoice.status?.toLowerCase() !== 'completed' || isAdmin || user?.role === 'admin') && (
                          <button
                            onClick={() => {
                              setEditingInvoice(invoice);
                              setShowEditModal(true);
                              setShowAddModal(false);
                              setNewInvoice({
                                invoiceNumber: invoice.invoiceNumber || '',
                                date: (invoice.status === 'blank' || isInvoiceBlank(invoice)) ? '' : (invoice.date ? invoice.date.slice(0, 10) : (invoice.createdAt ? new Date(invoice.createdAt).toISOString().split('T')[0] : '')),
                                customerId: customers.find(c => c.name === invoice.customer?.name)?._id || '',
                                customerAddress: invoice.customer?.address || '',
                                customerPhone: invoice.customer?.phone || '',
                                customerEmail: invoice.customer?.email || '',
                                projectId: invoice.project?._id || '',
                                projectAddress: projects.find(p => p._id === invoice.project?._id)?.projectAddress || '',
                                workOrderNumber: invoice.workOrder?.number || '',
                                workOrderDate: invoice.workOrder?.date ? invoice.workOrder.date.slice(0, 10) : '',
                                dispatchedThroughId: invoice.dispatchedThrough?.name ? (employees.find(e => e.name === invoice.dispatchedThrough?.name)?._id || '') : '',
                                dispatchedThroughPhone: invoice.dispatchedThrough?.phone || '',
                                deliveryAddress: invoice.deliveryAddress || '',
                                products: (invoice.items || []).map(item => {
                                  const itemProductId = (item as any).productId ?? ((item as any).product && (item as any).product._id) ?? '';
                                  const itemBarcode = (item as any).barcode ?? ((item as any).product && (item as any).product.barcode) ?? '';
                                  const itemName = (item as any).name ?? ((item as any).product && (item as any).product.name) ?? '';
                                  const itemUnit = (item as any).unit ?? ((item as any).product && (item as any).product.unit) ?? '';
                                  const qty = (item as any).qty ?? (item as any).quantity ?? 1;
                                  const price = (item as any).price ?? (item as any).unitPrice ?? 0;
                                  const discount = (item as any).discount ?? 0;
                                  return {
                                    productId: itemProductId,
                                    barcode: itemBarcode,
                                    name: itemName,
                                    unit: itemUnit,
                                    qty,
                                    price,
                                    amount: qty * price,
                                    discount,
                                    total: qty * price - discount,
                                    brand: (item as any).brand ?? '',
                                    model: (item as any).model ?? '',
                                    sku: (item as any).sku ?? '',
                                    type: (item as any).type ?? '',
                                    compressorType: (item as any).compressorType ?? '',
                                    countryOfOrigin: (item as any).countryOfOrigin ?? '',
                                    countryOfAssembly: (item as any).countryOfAssembly ?? '',
                                    details: (item as any).details ?? '',
                                    description: (item as any).description ?? '',
                                    capacity: (item as any).capacity ?? '',
                                    serialNo: (item as any).serialNo ?? '',
                                    partNo: (item as any).partNo ?? '',
                                    remarks: (item as any).remarks ?? '',
                                    warrantyDetails: (item as any).warrantyDetails ?? {
                                      hasWarranty: false,
                                      serviceWarranty: null,
                                      sparePartsWarranty: null,
                                      compressorWarranty: null,
                                      warranty: null
                                    },
                                  };
                                }),
                              });
                            }}
                              className="text-blue-600 hover:text-blue-800 font-['Times_New_Roman']"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          )}
                          {hasReturnedItems(invoice) && (
                            <button
                              onClick={() => {
                                // Navigate to return invoices page with this delivery invoice pre-selected
                                navigate(`/invoices/return?deliveryInvoiceId=${invoice._id}`);
                              }}
                              className="text-orange-600 hover:text-orange-800 font-['Times_New_Roman']"
                              title="View Returns"
                            >
                              <Undo2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedInvoiceForPrint(invoice);
                              setShowPrintPreview(true);
                            }}
                            className="text-green-600 hover:text-green-800 font-['Times_New_Roman']"
                            title="Print"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          {getEffectiveStatus(invoice) === 'processing' && (
                            <button 
                              onClick={() => handleStatusUpdate(invoice._id, 'sent')} 
                              className="text-red-600 hover:text-red-800 font-['Times_New_Roman']" 
                              title="Mark as Sent"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          {getEffectiveStatus(invoice) === 'sent' && isAdmin && (
                            <button 
                              onClick={() => handleStatusUpdate(invoice._id, 'completed')} 
                              className="text-green-600 hover:text-green-800 font-['Times_New_Roman']" 
                              title="Mark as Completed (Admin Only)"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {getEffectiveStatus(invoice) === 'sent' && !isAdmin && (
                            <span 
                              className="text-gray-400 font-['Times_New_Roman'] cursor-not-allowed" 
                              title="Only administrators can mark invoices as completed"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </span>
                          )}
                          <button
                            onClick={() => {
                              setEmailModalInvoice(invoice);
                            }}
                            className="text-green-600 hover:text-green-800 font-['Times_New_Roman']"
                            title="Email Invoice"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          {(hasWarrantyMap[invoice._id] || invoice.items?.some(item => (item as any).warrantyDetails?.hasWarranty)) && (
                            <button
                              onClick={() => openWarrantyModal(invoice)}
                              className="text-indigo-600 hover:text-indigo-800 font-['Times_New_Roman']"
                              title="View Warranties"
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </button>
                          )}
                          {/* History Button - Admin Only */}
                          {isAdmin && (
                            <button
                              onClick={() => {
                                setSelectedInvoiceForHistory(invoice);
                                setShowActivityHistoryModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-800 font-['Times_New_Roman']"
                              title="View Activity History (Admin Only)"
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDuplicateInvoice(invoice)}
                            className="text-teal-600 hover:text-teal-800 font-['Times_New_Roman']"
                            title="Duplicate"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteInvoice(invoice._id)}
                            className="text-red-600 hover:text-red-800 font-['Times_New_Roman']"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DoubleScrollbar>
          </div>
        </>
      )}

      {/* Add/Edit Invoice Modal */}
      <Modal
        isOpen={showAddModal || showEditModal}
        onClose={() => { setShowAddModal(false); setShowEditModal(false); }}
        title={showAddModal ? 'Add Delivery Invoice' : 'Edit Delivery Invoice'}
        size="xl"
      >
        <div className="max-w-none w-full mx-auto space-y-8 p-2 md:p-8" style={{ maxWidth: '3840px' } as any}>
          {/* Required Fields Note */}
          <div className="text-sm text-[var(--color-text-secondary)] mb-4 font-['Times_New_Roman']">
            Fields marked with <span className="text-red-500">*</span> are required
          </div>
          
          {/* Invoice Number and Date */}
          <div className="flex flex-col md:flex-row md:items-center md:space-x-8 mb-4">
            <div className="mb-2 md:mb-0">
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">
                Delivery Invoice Number <span className="text-red-500">*</span>
              </label>
              <Input value={newInvoice.invoiceNumber || 'DINV-0001001'} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">
                Date {!(addedProducts.length > 0 && addedProducts.every(p => !p.productId || p.productId.trim() === '')) && <span className="text-red-500">*</span>}
              </label>
              <Input
                type="date"
                value={newInvoice.date}
                onChange={e => setNewInvoice(prev => ({ ...prev, date: e.target.value }))}
                className={`bg-[var(--color-surface-light)] ${formErrors.date ? 'border-[var(--color-danger)]' : ''}`}
              />
              {formErrors.date && <div className="text-[var(--color-danger)] text-xs mt-1">{formErrors.date}</div>}
            </div>
          </div>

          {/* Customer Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <select
                value={newInvoice.customerId}
                onChange={e => handleCustomerChange(e.target.value)}
                className={`w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] ${formErrors.customer ? 'border-[var(--color-danger)]' : ''}`}
              >
                <option value="">Select Customer</option>
                {(customers || []).map(customer => (
                  <option key={customer._id} value={customer._id}>{customer.name}</option>
                ))}
              </select>
              {formErrors.customer && <div className="text-[var(--color-danger)] text-xs mt-1">{formErrors.customer}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Customer Address</label>
              <Input value={newInvoice.customerAddress} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Customer Phone</label>
              <Input value={newInvoice.customerPhone} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Customer Email</label>
              <Input value={newInvoice.customerEmail} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
          </div>

          {/* Project Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                value={newInvoice.projectId}
                onChange={e => handleProjectChange(e.target.value)}
                className={`w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] ${formErrors.project ? 'border-[var(--color-danger)]' : ''}`}
              >
                <option value="">Select Project</option>
                {addModalFilteredProjects.map(project => (
                  <option key={project._id} value={project._id}>{project.name}</option>
                ))}
              </select>
              {formErrors.project && <div className="text-[var(--color-danger)] text-xs mt-1">{formErrors.project}</div>}
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Project Address</label>
              <Input value={newInvoice.projectAddress} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Work Order Number</label>
              <select
                value={newInvoice.workOrderNumber}
                onChange={e => handleWorkOrderChange(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                disabled={!newInvoice.projectId}
              >
                <option value="">Select Work Order</option>
                {(projects.find(p => p._id === newInvoice.projectId)?.workOrders || []).map(wo => (
                  <option key={wo.number} value={wo.number}>{wo.number}</option>
                ))}
              </select>
              {newInvoice.projectId && (projects.find(p => p._id === newInvoice.projectId)?.workOrders || []).length === 0 && (
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">No work orders available for this project</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Work Order Date</label>
              <Input value={newInvoice.workOrderDate} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
          </div>

          {/* Employee and Delivery Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Dispatched Through</label>
              <select
                value={newInvoice.dispatchedThroughId}
                onChange={e => handleEmployeeChange(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
              >
                <option value="">Select Employee</option>
                {(employees || []).map(employee => (
                  <option key={employee._id} value={employee._id}>{employee.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Mobile No.</label>
              <Input value={newInvoice.dispatchedThroughPhone} readOnly className="bg-[var(--color-surface-light)]" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Delivery Address</label>
              <Input 
                value={newInvoice.deliveryAddress} 
                onChange={e => setNewInvoice(prev => ({ ...prev, deliveryAddress: e.target.value }))}
                placeholder="Enter delivery address"
                className="w-full"
              />
            </div>
          </div>

          {/* Load from Purchase Invoice (Add modal only) */}
          {showAddModal && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Load from Purchase Invoice</label>
              <select
                value={selectedPurchaseInvoiceId}
                onChange={(e) => handleLoadFromPurchaseInvoice(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                disabled={loadingPurchaseInvoices}
              >
                <option value="">Select a purchase invoice to load products…</option>
                {purchaseInvoicesForLoad.map((inv) => (
                  <option key={inv._id} value={inv._id}>
                    {inv.invoiceNumber} — {(inv.customerName || inv.customer?.name) || '—'} — {inv.date ? new Date(inv.date).toLocaleDateString() : (inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—')}
                  </option>
                ))}
              </select>
              {loadingPurchaseInvoices && (
                <span className="text-xs text-[var(--color-text-secondary)] mt-1 block">Loading purchase invoices…</span>
              )}
            </div>
          )}

          {/* Product Entry Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                Add Product <span className="text-red-500">*</span>
              </label>
            </div>
            <DoubleScrollbar>
              {/* First Row - Product Identification */}
              <table className="min-w-full border border-[var(--color-border)] bg-[var(--color-surface)]">
                <thead className="bg-[var(--color-surface-light)]">
                  <tr>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[80px]">Barcode</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[120px]">Parent Category</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[120px]">Sub Category</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[220px]">Product Details</th>
                    <th className="border border-[var(--color-border)] px-1 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[48px]">Unit</th>
                    <th className="border border-[var(--color-border)] px-1 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman'] min-w-[48px]">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-[var(--color-background)]">
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[80px]">
                      <div className="flex gap-1">
                        <Input
                          value={currentProduct.barcode}
                          onChange={e => handleCurrentProductChange('barcode', e.target.value)}
                          placeholder="Barcode"
                          className="text-xs placeholder:text-[var(--color-text-secondary)] flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => setShowBarcodeScanner(true)}
                          className="px-2 py-1 bg-[var(--color-primary)] text-white rounded text-xs whitespace-nowrap"
                          title="Scan Barcode"
                        >
                          Scan
                        </button>
                      </div>
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[120px]">
                      <select
                        value={selectedParentCategory}
                        onChange={e => handleParentCategoryChange(e.target.value)}
                        className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                      >
                        <option value="">Select Parent Category</option>
                        {parentCategories.map(category => (
                          <option key={category._id} value={category._id}>{category.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[120px]">
                      <select
                        value={selectedSubCategory}
                        onChange={e => handleSubCategoryChange(e.target.value)}
                        className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman']"
                      >
                        <option value="">Select Sub Category</option>
                        {subCategories
                          .filter(cat => {
                            // Handle both string and ObjectId parentCategory
                            const parentCategoryId = typeof cat.parentCategory === 'object' && cat.parentCategory !== null 
                              ? (cat.parentCategory as any)._id || (cat.parentCategory as any).toString()
                              : cat.parentCategory;
                            const matches = !selectedParentCategory || parentCategoryId === selectedParentCategory;
                            return matches;
                          })
                          .map(category => (
                            <option key={category._id} value={category._id}>{category.name}</option>
                          ))}
                      </select>
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top min-w-[220px] relative">
                      <div className="relative" ref={productDropdownRef}>
                        <Input
                          ref={productInputRef}
                          value={productSearchTerm}
                          onChange={e => handleProductSearch(e.target.value)}
                          onFocus={() => {
                            updateDropdownPosition();
                            setShowProductDropdown(true);
                          }}
                          placeholder="Type to search products..."
                          className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] placeholder:text-[var(--color-text-secondary)]"
                          style={{ fontFamily: 'var(--font-family)' } as any}
                        />
                      </div>
                      {currentProduct.productId && (
                        <div className="mt-1 text-xs text-[var(--color-text-secondary)] font-['Times_New_Roman']">{products.find(p => p._id === currentProduct.productId)?.description || ''}</div>
                      )}
                    </td>
                    <td className="border border-[var(--color-border)] px-1 py-1 align-top min-w-[48px]">
                      {(() => {
                        const curProd = currentProduct.productId ? products.find((p: any) => p._id === currentProduct.productId) : null;
                        const availableUnits = (curProd as any)?.units?.length ? (curProd as any).units : (curProd?.unit ? [curProd.unit] : null);
                        if (availableUnits && availableUnits.length > 1) {
                          return (
                            <select
                              value={currentProduct.unit || availableUnits[0]}
                              onChange={(e) => handleCurrentProductChange('unit', e.target.value)}
                              className="w-full px-1 py-1 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)]"
                              style={{ fontFamily: 'var(--font-family)' } as any}
                            >
                              {availableUnits.map((u: string) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          );
                        }
                        return <Input value={currentProduct.unit} readOnly className="text-xs" />;
                      })()}
                    </td>
                    <td className="border border-[var(--color-border)] px-1 py-1 align-top min-w-[48px]">
                      <Input
                        type="number"
                        min={1}
                        value={getInputValue('qty')}
                        onChange={e => handleCurrentProductChange('qty', e.target.value)}
                        onFocus={() => handleInputFocus('qty')}
                        className="text-xs"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Second Row - Financial Details */}
              <table className="min-w-full border border-[var(--color-border)] bg-[var(--color-surface)] mt-2">
                <thead className="bg-[var(--color-surface-light)]">
                  <tr>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Price</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Amount</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Discount</th>
                    <th className="border border-[var(--color-border)] px-2 py-1 text-left text-xs font-medium text-[var(--color-text-secondary)] font-['Times_New_Roman']">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-[var(--color-background)]">
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input
                        type="number"
                        min={0}
                        value={getInputValue('price')}
                        onChange={e => handleCurrentProductChange('price', e.target.value)}
                        onFocus={() => handleInputFocus('price')}
                        className="text-xs"
                      />
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input value={currentProduct.amount} readOnly className="text-xs" />
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input
                        type="number"
                        min={0}
                        value={getInputValue('discount')}
                        onChange={e => handleCurrentProductChange('discount', e.target.value)}
                        onFocus={() => handleInputFocus('discount')}
                        className="text-xs"
                      />
                    </td>
                    <td className="border border-[var(--color-border)] px-2 py-1 align-top">
                      <Input value={currentProduct.total} readOnly className="text-xs" />
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Warranty Details Checkbox */}
              <div className="mt-3 flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasWarranty"
                  checked={currentProduct.warrantyDetails?.hasWarranty || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCurrentProduct(prev => ({
                      ...prev,
                      warrantyDetails: {
                        ...(prev.warrantyDetails || {
                          serviceWarranty: null,
                          sparePartsWarranty: null,
                          compressorWarranty: null,
                          warranty: null
                        }),
                        hasWarranty: checked
                      }
                    }));
                  }}
                  className="w-4 h-4 text-[var(--color-primary)] border-[var(--color-border)] rounded focus:ring-[var(--color-primary)]"
                />
                <label htmlFor="hasWarranty" className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman'] cursor-pointer">
                  Warranty Details
                </label>
              </div>

              {/* Conditional Warranty Fields */}
              {currentProduct.warrantyDetails?.hasWarranty && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 p-3 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-light)]">
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">Service Warranty (Years)</label>
                    <Input
                      type="number"
                      value={currentProduct.warrantyDetails?.serviceWarranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setCurrentProduct(prev => ({
                          ...prev,
                          warrantyDetails: { ...prev.warrantyDetails!, serviceWarranty: val }
                        }));
                      }}
                      placeholder="Years"
                      className="text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">Spare Parts (Years)</label>
                    <Input
                      type="number"
                      value={currentProduct.warrantyDetails?.sparePartsWarranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setCurrentProduct(prev => ({
                          ...prev,
                          warrantyDetails: { ...prev.warrantyDetails!, sparePartsWarranty: val }
                        }));
                      }}
                      placeholder="Years"
                      className="text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">Compressor (Years)</label>
                    <Input
                      type="number"
                      value={currentProduct.warrantyDetails?.compressorWarranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setCurrentProduct(prev => ({
                          ...prev,
                          warrantyDetails: { ...prev.warrantyDetails!, compressorWarranty: val }
                        }));
                      }}
                      placeholder="Years"
                      className="text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">Warranty (Years)</label>
                    <Input
                      type="number"
                      value={currentProduct.warrantyDetails?.warranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setCurrentProduct(prev => ({
                          ...prev,
                          warrantyDetails: { ...prev.warrantyDetails!, warranty: val }
                        }));
                      }}
                      placeholder="Years"
                      className="text-xs"
                    />
                  </div>
                </div>
              )}

              {/* Note Field */}
              <div className="mt-3">
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Note (Optional)</label>
                <textarea
                  value={currentProduct.remarks || ''}
                  onChange={e => setCurrentProduct(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Enter any additional notes for this product..."
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] text-xs font-['Times_New_Roman'] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  rows={2}
                />
              </div>
            </DoubleScrollbar>
            <div className="flex justify-end mt-2">
              <div className="flex space-x-2">
                <button
                  onClick={handleAddProduct}
                  className="px-3 py-1.5 text-sm bg-white text-[var(--color-text)] border border-[var(--color-primary)] font-semibold rounded-lg transition-colors font-['Times_New_Roman'] hover:text-[var(--color-primary)] hover:bg-white focus:text-[var(--color-primary)] focus:bg-white"
                  style={{ fontFamily: 'var(--font-family)' } as any}
                >
                  Add Product to List
                </button>
                <button
                  onClick={handleAddBlankRows}
                  className="px-3 py-1.5 text-sm bg-white text-[var(--color-text)] border border-[var(--color-primary)] font-semibold rounded-lg transition-colors font-['Times_New_Roman'] hover:text-[var(--color-primary)] hover:bg-white focus:text-[var(--color-primary)] focus:bg-white"
                  style={{ fontFamily: 'var(--font-family)' } as any}
                >
                  Record Blank
                </button>
                <button
                  onClick={() => setShowAddProductModal(true)}
                  className="px-3 py-1.5 text-sm bg-[var(--color-primary)] text-white border border-[var(--color-primary)] font-semibold rounded-lg transition-colors font-['Times_New_Roman'] hover:bg-[var(--color-primary-dark)] hover:text-white focus:bg-[var(--color-primary-dark)] focus:text-white flex items-center space-x-1"
                  style={{ fontFamily: 'var(--font-family)' } as any}
                >
                  <Package size={14} />
                  <span>New Product</span>
                </button>
              </div>
            </div>
            {/* Show warning in Add Product section */}
            {insufficientStockWarning && (
              <div className="text-red-600 font-semibold mt-2">{insufficientStockWarning}</div>
            )}
          </div>
          {/* Added Products List Section */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Added Products</label>
            <DoubleScrollbar>
              <table className="w-full min-w-0 divide-y divide-[var(--color-border)] text-sm">
                <thead className="bg-[var(--color-surface-light)]">
                  <tr>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">
                      <input type="checkbox" className="form-checkbox" checked={selectedRows.length === addedProducts.length && addedProducts.length > 0} onChange={handleSelectAll} />
                    </th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">SL</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top min-w-[32px] md:min-w-[40px]">Barcode</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top min-w-[140px] md:min-w-[180px] lg:min-w-[220px]">Product Details</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Unit</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Qty</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Price</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Amount</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Discount</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Total</th>
                    <th className="px-2 py-2 text-xs font-medium text-[var(--color-text-secondary)] align-top">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
                  {addedProducts.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-6 text-[var(--color-text-secondary)]">No products added yet.</td>
                    </tr>
                  )}
                  {(addedProducts || []).map((prod, idx) => {
                    const selectedProduct = products.find(p => p._id === prod.productId);
                    const custom = selectedProduct?.customFields || {};
                    const isBlankRow = !prod.productId || prod.productId.trim() === '';
                    
                    return (
                      <tr key={idx}>
                        <td className="px-2 py-2 text-center align-top">
                          <input type="checkbox" className="form-checkbox" checked={selectedRows.includes(idx)} onChange={() => handleSelectRow(idx)} />
                        </td>
                        <td className="px-2 py-2 text-center align-top">{isBlankRow ? '' : idx + 1}</td>
                        <td className="px-2 py-2 text-center align-top text-[10px] min-w-[32px] md:min-w-[40px]">{isBlankRow ? '' : prod.barcode}</td>
                        <td className="px-2 py-2 align-top text-sm min-w-[140px] md:min-w-[180px] lg:min-w-[220px]">
                          {isBlankRow ? '' : renderProductDetails(prod, selectedProduct)}
                        </td>
                        <td className="px-2 py-2 text-center align-top">
                          {isBlankRow ? '' : selectedProduct && (selectedProduct as any).units?.length > 1 ? (
                            <select
                              value={prod.unit || (selectedProduct as any).units[0]}
                              onChange={(e) => {
                                const newUnit = e.target.value;
                                setAddedProducts((prev) => prev.map((p, i) => i === idx ? { ...p, unit: newUnit } : p));
                              }}
                              className="px-1 py-0.5 border border-[var(--color-border)] text-xs bg-[var(--color-surface)] text-[var(--color-text)]"
                              style={{ fontFamily: 'var(--font-family)' } as any}
                            >
                              {(selectedProduct as any).units.map((u: string) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          ) : (
                            prod.unit
                          )}
                        </td>
                        <td className="px-2 py-2 text-center align-top">{isBlankRow ? '' : prod.qty}</td>
                        <td className="px-2 py-2 text-center align-top">{isBlankRow ? '' : safeCurrency(prod.price)}</td>
                        <td className="px-2 py-2 text-center align-top">{isBlankRow ? '' : safeCurrency(prod.amount)}</td>
                        <td className="px-2 py-2 text-center align-top">{isBlankRow ? '' : safeCurrency(prod.discount)}</td>
                        <td className="px-2 py-2 text-center align-top">{isBlankRow ? '' : safeCurrency(prod.total)}</td>
                        <td className="px-2 py-2 text-center align-top">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              className="text-blue-600 hover:text-blue-800 font-['Times_New_Roman'] px-2 py-1 border border-blue-600 rounded"
                              onClick={() => handleEditProduct(idx)}
                            >
                              Edit
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800 font-['Times_New_Roman'] px-2 py-1 border border-red-600 rounded ml-2"
                              onClick={() => handleRemoveProduct(idx)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DoubleScrollbar>
            {selectedRows.length > 0 && (
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm text-[var(--color-text)]">Bulk Action:</span>
                <button onClick={handleBulkDelete} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs">Delete</button>
                <span className="text-xs text-[var(--color-text-secondary)]">({selectedRows.length} selected)</span>
              </div>
            )}
            {/* Display product errors */}
            {formErrors.products.length > 0 && (
              <div className="mt-2">
                {formErrors.products.map((error, idx) => (
                  <div key={idx} className="text-[var(--color-danger)] text-sm font-semibold">{error}</div>
                ))}
              </div>
            )}
          </div>
          {/* Total Amount */}
          <div className="flex justify-end mt-6">
            <div className="text-xl font-bold text-[var(--color-text)]">
              Total: {safeCurrency(addedProducts.reduce((sum, p) => sum + (p.total || 0), 0))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-8">
            <button
              onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              className="px-5 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveInvoice}
              className="px-5 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg transition-colors font-['Times_New_Roman'] border border-[var(--color-primary)] hover:bg-white hover:text-[var(--color-primary)] focus:bg-white focus:text-[var(--color-primary)]"
              disabled={saving}
              style={{ fontFamily: 'var(--font-family)' } as any}
            >
              {saving ? 'Saving...' : 'Save Invoice'}
            </button>
          </div>
        </div>
      </Modal>

      {/* View Invoice Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => { setShowViewModal(false); }}
        size="xl"
        headerCenter={<span>View Delivery Invoice</span>}
        headerRight={
          <div className="flex items-center gap-2 print:hidden" style={{ userSelect: 'none' } as any}>
            <button
              className="no-print"
              style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', fontFamily: 'var(--font-family)' } as any}
              onClick={async () => {
                setShowPrintOverlay(false);
                await new Promise(res => setTimeout(res, 50));
                setShowPrintOverlay(true);
              }}
            >
              Print
            </button>
            {/* Pagination controls - always visible */}
            {viewInvoice && viewInvoice.items && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPrintCurrentPage(p => Math.max(1, p - 1))}
                  disabled={printCurrentPage === 1}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                {Array.from({ length: Math.ceil(viewInvoice.items.length / itemsPerPage) }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPrintCurrentPage(i + 1)}
                    className={`px-2 py-1 border rounded ${printCurrentPage === i + 1 ? 'bg-blue-500 text-white' : ''}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPrintCurrentPage(p => Math.min(Math.ceil(viewInvoice.items.length / itemsPerPage), p + 1))}
                  disabled={printCurrentPage === Math.ceil(viewInvoice.items.length / itemsPerPage)}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        }
        className="print:hidden"
      >
        {viewInvoice ? (
          <DeliveryInvoicePrintView invoice={viewInvoice} settings={settings} currentPage={printCurrentPage} itemsPerPage={itemsPerPage} printMode={true} />
        ) : (
          <div>Loading...</div>
        )}
      </Modal>

      <PrintPreviewModal
        isOpen={showPrintPreview}
        onClose={() => {
          setShowPrintPreview(false);
          setSelectedInvoiceForPrint(null);
        }}
        invoiceId={selectedInvoiceForPrint?._id || ''}
        invoiceType="delivery"
        invoiceData={selectedInvoiceForPrint}
        settings={settings}
      />

      {/* Multiple Print Preview Modal */}
      <PrintPreviewModal
        isOpen={showMultiplePrintPreview}
        onClose={() => {
          setShowMultiplePrintPreview(false);
          setSelectedInvoicesForPrint([]);
        }}
        invoiceId=""
        invoiceType="delivery"
        invoicesData={selectedInvoicesForPrint}
        settings={settings}
      />

      {/* Edit Product Modal */}
      <Modal
        isOpen={editProductIdx !== null}
        onClose={() => setEditProductIdx(null)}
        title="Edit Product"
      >
        {editProductData && (() => {
          // Move these calculations above the return
          const qty = Number(editProductData.qty) || 0;
          const price = Number(editProductData.price) || 0;
          const discount = Number(editProductData.discount) || 0;
          const amount = qty * price;
          const total = amount - discount;
          return (
            <div className="space-y-4">
              {/* Product Name (read-only) */}
              <div>
                <label className="block text-sm font-medium mb-1">Product</label>
                <input
                  type="text"
                  value={editProductData.name || ''}
                  disabled
                  className="w-full border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
                />
              </div>
              {/* Qty */}
              <div>
                <label className="block text-sm font-medium mb-1">Qty</label>
                <input
                  type="number"
                  min={1}
                  value={editProductData.qty || ''}
                  onChange={e => handleEditProductChange('qty', Number(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              {/* Price */}
              <div>
                <label className="block text-sm font-medium mb-1">Price</label>
                <input
                  type="number"
                  min={0}
                  value={editProductData.price || ''}
                  onChange={e => handleEditProductChange('price', Number(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              {/* Discount */}
              <div>
                <label className="block text-sm font-medium mb-1">Discount</label>
                <input
                  type="number"
                  min={0}
                  value={editProductData.discount || ''}
                  onChange={e => handleEditProductChange('discount', Number(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              {/* Amount (read-only, auto-calculated) */}
              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <input
                  type="number"
                  value={amount}
                  disabled
                  className="w-full border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
                />
              </div>
              {/* Total (read-only, auto-calculated) */}
              <div>
                <label className="block text-sm font-medium mb-1">Total</label>
                <input
                  type="number"
                  value={total}
                  disabled
                  className="w-full border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
                />
              </div>

              {/* Warranty Details Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="editHasWarranty"
                  checked={editProductData.warrantyDetails?.hasWarranty || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditProductData(prev => ({
                      ...prev!,
                      warrantyDetails: {
                        ...(prev!.warrantyDetails || {
                          serviceWarranty: null,
                          sparePartsWarranty: null,
                          compressorWarranty: null,
                          warranty: null
                        }),
                        hasWarranty: checked
                      }
                    }));
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="editHasWarranty" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Warranty Details
                </label>
              </div>

              {/* Conditional Warranty Fields */}
              {editProductData.warrantyDetails?.hasWarranty && (
                <div className="grid grid-cols-2 gap-4 p-3 border rounded-lg bg-gray-50">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Service Warranty (Years)</label>
                    <input
                      type="number"
                      value={editProductData.warrantyDetails?.serviceWarranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setEditProductData(prev => ({
                          ...prev!,
                          warrantyDetails: { ...prev!.warrantyDetails!, serviceWarranty: val }
                        }));
                      }}
                      className="w-full border rounded px-2 py-1 text-sm"
                      placeholder="Years"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Spare Parts (Years)</label>
                    <input
                      type="number"
                      value={editProductData.warrantyDetails?.sparePartsWarranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setEditProductData(prev => ({
                          ...prev!,
                          warrantyDetails: { ...prev!.warrantyDetails!, sparePartsWarranty: val }
                        }));
                      }}
                      className="w-full border rounded px-2 py-1 text-sm"
                      placeholder="Years"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Compressor (Years)</label>
                    <input
                      type="number"
                      value={editProductData.warrantyDetails?.compressorWarranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setEditProductData(prev => ({
                          ...prev!,
                          warrantyDetails: { ...prev!.warrantyDetails!, compressorWarranty: val }
                        }));
                      }}
                      className="w-full border rounded px-2 py-1 text-sm"
                      placeholder="Years"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Warranty (Years)</label>
                    <input
                      type="number"
                      value={editProductData.warrantyDetails?.warranty ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : Number(e.target.value);
                        setEditProductData(prev => ({
                          ...prev!,
                          warrantyDetails: { ...prev!.warrantyDetails!, warranty: val }
                        }));
                      }}
                      className="w-full border rounded px-2 py-1 text-sm"
                      placeholder="Years"
                    />
                  </div>
                </div>
              )}

              {/* Note Field */}
              <div>
                <label className="block text-sm font-medium mb-1">Note (Optional)</label>
                <textarea
                  value={editProductData.remarks || ''}
                  onChange={e => setEditProductData(prev => ({ ...prev!, remarks: e.target.value }))}
                  placeholder="Enter any additional notes for this product..."
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  className="px-4 py-2 bg-gray-200 rounded"
                  onClick={() => setEditProductIdx(null)}
                >Cancel</button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                  onClick={handleSaveEditProduct}
                >Save</button>
              </div>
            </div>
          );
        })()}
        {/* Show warning in Edit Product modal */}
        {editProductData && insufficientStockWarning && (
          <div className="text-red-600 font-semibold mt-2">{insufficientStockWarning}</div>
        )}
      </Modal>

      {/* Barcode Scanner Modal at the end of the component JSX */}
      {showBarcodeScanner && (
        <Modal
          isOpen={true}
          onClose={async () => {
            if (scannerRef.current && scannerRef.current.stopCamera) {
              await scannerRef.current.stopCamera();
            }
            setShowBarcodeScanner(false);
          }}
          title="Scan Barcode"
        >
          <div className="flex flex-col items-center justify-center p-4 w-full">
            <BarcodeScanner
              ref={scannerRef}
              onDetected={async (barcode) => {
                if (scannerRef.current && scannerRef.current.stopCamera) {
                  await scannerRef.current.stopCamera();
                }
                handleCurrentProductChange('barcode', barcode);
                setShowBarcodeScanner(false);
                toast.success('Barcode scanned');
              }}
              onClose={async () => {
                if (scannerRef.current && scannerRef.current.stopCamera) {
                  await scannerRef.current.stopCamera();
                }
                setShowBarcodeScanner(false);
              }}
            />
          </div>
        </Modal>
      )}

      {/* Email Compose Modal */}
      <EmailComposeModal
        isOpen={!!emailModalInvoice}
        onClose={() => setEmailModalInvoice(null)}
        onSend={async (emailData) => {
          setSending(true);
          try {
            await apiService.sendInvoiceEmail({
              to: emailData.to.map(r => r.email).join(','),
              invoiceData: {
                invoice: emailModalInvoice,
                subject: emailData.subject,
                message: emailData.message
              },
              invoiceType: 'delivery'
            });
            setEmailModalInvoice(null);
            setSending(false);
            toast.success('Email sent successfully!');
          } catch (e) {
            setSending(false);
            toast.error('Failed to send email.');
          }
        }}
        defaultRecipients={emailModalInvoice?.customer?.email ? [{ email: emailModalInvoice.customer.email, name: emailModalInvoice.customer.name }] : []}
        defaultSubject="Your Delivery Invoice"
        defaultMessage="Please find your delivery invoice below."
        invoicePreview={emailModalInvoice && (
          <DeliveryInvoicePrintView
            invoice={emailModalInvoice}
            settings={{}}
            currentPage={1}
            itemsPerPage={14}
            printMode={true}
          />
        )}
        sending={sending}
      />

      {/* Warranty Details Modal */}
      <Modal
        isOpen={showWarrantyModal}
        onClose={() => {
          setShowWarrantyModal(false);
          setSelectedInvoiceForWarranty(null);
          setInvoiceWarranties([]);
        }}
        title={`Warranties for Invoice ${selectedInvoiceForWarranty?.invoiceNumber || ''}`}
        size="xl"
      >
        <div className="space-y-4">
          {loadingWarranties ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : invoiceWarranties.length > 0 ? (
            <div className="space-y-6">
              {Object.values(
                invoiceWarranties.reduce((acc: Record<string, { product: any, brand: string, model: string, type: string, compressorType: string, serviceYears: number, spareYears: number, compressorYears: number, generalYears: number, items: WarrantyItem[] }>, warranty) => {
                  const key = warranty.product._id + (warranty.brand || '') + (warranty.model || '');
                  if (!acc[key]) {
                    acc[key] = {
                      product: warranty.product,
                      brand: warranty.brand || warranty.product.customFields?.brand || 'N/A',
                      model: warranty.model || warranty.product.customFields?.model || 'N/A',
                      type: warranty.type || warranty.product.customFields?.type || 'N/A',
                      compressorType: warranty.compressorType || warranty.product.customFields?.compressorType || 'N/A',
                      serviceYears: warranty.serviceWarrantyYears || 0,
                      spareYears: warranty.sparePartsWarrantyYears || 0,
                      compressorYears: warranty.compressorWarrantyYears || 0,
                      generalYears: warranty.warrantyYears || 0,
                      items: []
                    };
                  }
                  acc[key].items.push(warranty);
                  return acc;
                }, {})
              ).map((group, gIdx) => (
                <div key={gIdx} className="border border-[var(--color-border)] rounded-lg overflow-hidden shadow-sm bg-[var(--color-surface)]">
                  {/* Product Header Row */}
                  <div className="bg-[var(--color-background)] px-4 py-3 border-b border-[var(--color-border)]">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-sm font-bold text-[var(--color-text)]">{group.product?.name || 'Unknown Product'}</h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-[var(--color-text-secondary)]">
                          <span><span className="font-bold uppercase text-[9px] opacity-75 mr-1">Brand:</span> {group.brand}</span>
                          <span><span className="font-bold uppercase text-[9px] opacity-75 mr-1">Model:</span> {group.model}</span>
                          {group.type !== 'N/A' && <span><span className="font-bold uppercase text-[9px] opacity-75 mr-1">Type:</span> {group.type}</span>}
                          {group.compressorType !== 'N/A' && <span><span className="font-bold uppercase text-[9px] opacity-75 mr-1">Compressor:</span> {group.compressorType}</span>}
                          <span><span className="font-bold uppercase text-[9px] opacity-75 mr-1">Total Qty:</span> {group.items.reduce((sum, it) => sum + (it.quantity || 1), 0)}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[10px]">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded border border-blue-100 dark:border-blue-800 font-bold">Service: {group.serviceYears}Y</span>
                        <span className="px-2 py-1 bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded border border-green-100 dark:border-green-800 font-bold">Spare: {group.spareYears}Y</span>
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 rounded border border-purple-100 dark:border-purple-800 font-bold">Compressor: {group.compressorYears}Y</span>
                        <span className="px-2 py-1 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 rounded border border-indigo-100 dark:border-indigo-800 font-bold">General: {group.generalYears}Y</span>
                      </div>
                    </div>
                  </div>

                  {/* Serial Numbers Sub-list */}
                  <DoubleScrollbar>
                    <table className="min-w-full divide-y divide-[var(--color-border)]">
                      <thead className="bg-[var(--color-surface)]">
                        <tr>
                          <th className="px-6 py-2 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider w-1/2">SL (Serial Number)</th>
                          <th className="px-6 py-2 text-center text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Status</th>
                          <th className="px-6 py-2 text-right text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
                        {group.items.map((warranty, wIdx) => (
                          <tr key={warranty._id} className="hover:bg-[var(--color-surface-hover)] transition-colors">
                            <td className="px-6 py-3 whitespace-nowrap">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-[10px] font-bold text-[var(--color-text-secondary)] bg-[var(--color-background)] w-6 h-6 rounded-full flex items-center justify-center">
                                    {wIdx + 1}
                                  </span>
                                  <a 
                                    href={`${window.location.origin}/CSPortal/Warranty/${warranty.serialNo}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="font-mono text-sm font-bold text-[var(--color-primary)] tracking-wider hover:underline cursor-pointer flex items-center gap-1"
                                    title="View in Customer Portal"
                                  >
                                    SL: {warranty.serialNo || 'NO SERIAL'}
                                    <ExternalLink size={12} className="opacity-50" />
                                  </a>
                                </div>
                                <div className="ml-9 flex gap-3 text-[9px] text-[var(--color-text-secondary)] font-medium">
                                  <span>Type: {warranty.type || warranty.product?.customFields?.type || '—'}</span>
                                  <span>Compressor: {warranty.compressorType || warranty.product?.customFields?.compressorType || '—'}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-center">
                              <span className={`px-2 py-0.5 text-[9px] font-black rounded-full uppercase tracking-tighter ${
                                warranty.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800' :
                                warranty.status === 'expiring_soon' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800' :
                                'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border border-red-200 dark:border-red-800'
                              }`}>
                                {warranty.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  icon={<Eye className="w-3 h-3" />}
                                  className="!py-1 !text-[10px]"
                                  onClick={() => {
                                    setSelectedWarrantyForPrint(warranty);
                                    setShowWarrantyPrintPreview(true);
                                    setShowWarrantyModal(false);
                                  }}
                                >
                                  View Card
                                </Button>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  icon={<Printer className="w-3 h-3" />}
                                  className="!py-1 !text-[10px]"
                                  onClick={async () => {
                                    // Use the updated print utility directly for a consistent experience
                                    const printData = {
                                      ...warranty,
                                      customer: selectedInvoiceForWarranty?.customer || warranty.customer,
                                      project: selectedInvoiceForWarranty?.project || warranty.project,
                                      deliveryInvoice: {
                                        date: selectedInvoiceForWarranty?.date,
                                        createdAt: selectedInvoiceForWarranty?.createdAt
                                      }
                                    };
                                    await printWarrantyCard(printData as any, settings);
                                  }}
                                >
                                  Print Card
                                </Button>                                <button
                                  className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 text-[10px] font-bold text-gray-600 transition-colors"
                                  onClick={() => {
                                    // Logic for barcode printing - A4, 4 copies, horizontal
                                    const printWindow = window.open('', '_blank');
                                    if (printWindow) {
                                      const canvas = document.createElement('canvas');
                                      JsBarcode(canvas, warranty.serialNo || 'N/A', {
                                        format: "CODE128",
                                        width: 2,
                                        height: 80,
                                        displayValue: true,
                                        fontSize: 20,
                                        font: "serif"
                                      });
                                      const barcodeUrl = canvas.toDataURL();
                                      
                                      const barcodeItem = `
                                        <div style="width: 2.1in; border: 0.1mm dashed #ccc; padding: 3mm; display: flex; flex-direction: column; align-items: center; box-sizing: border-box; margin: 0;">
                                          <img src="${barcodeUrl}" style="width: 1.8in; height: auto;" />
                                          <div style="font-size: 10px; margin-top: 1mm; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 100%;">${warranty.product.name}</div>
                                        </div>
                                      `;

                                      printWindow.document.write(`<!DOCTYPE html><html lang="en">
                                          <head>
                                            <meta charset="UTF-8">
                                            <title>Print Barcodes - ${warranty.serialNo}</title>
                                            <style>
                                              @page {
                                                size: A4;
                                                margin: 10mm;
                                              }
                                              body {
                                                margin: 0;
                                                padding: 0;
                                                font-family: 'Times New Roman', serif;
                                              }
                                              .page-container {
                                                width: 100%;
                                                display: flex;
                                                justify-content: center;
                                                padding-top: 0.10in;
                                              }
                                              .grid-container {
                                                display: grid;
                                                grid-template-columns: repeat(2, 2.1in);
                                                grid-template-rows: repeat(2, auto);
                                                border: none;
                                                width: 4.2in;
                                              }
                                              @media print {
                                                .page-container {
                                                  width: 210mm;
                                                }
                                              }
                                            </style>
                                          </head>
                                          <body>
                                            <div class="page-container">
                                              <div class="grid-container">
                                                ${barcodeItem}
                                                ${barcodeItem}
                                                ${barcodeItem}
                                                ${barcodeItem}
                                              </div>
                                            </div>
                                            <script>
                                              window.onload = () => {
                                                setTimeout(() => {
                                                  window.print();
                                                  window.close();
                                                }, 500);
                                              };
                                            </script>
                                          </body>
                                        </html>
                                      `);
                                      printWindow.document.close();
                                    }
                                  }}
                                >
                                  <Printer size={12} />
                                  Barcode
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </DoubleScrollbar>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 font-medium">No warranty records found for this invoice.</div>
              <p className="text-sm text-gray-400 mt-1">Warranty records are created automatically when saving a delivery invoice with warranty details.</p>
            </div>
          )}
          <div className="flex justify-end pt-4 space-x-2">
            {invoiceWarranties && invoiceWarranties.length > 0 && (
              <>
                <Button 
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    if (printWindow) {
                      const warranties = invoiceWarranties;
                      let allHtml = '';

                      // Create a container to hold all barcode sets
                      allHtml += `<!DOCTYPE html><html lang="en">
                          <head>
                            <meta charset="UTF-8">
                            <title>Print All Barcodes</title>
                            <style>
                              @page {
                                size: A4;
                                margin: 10mm;
                              }
                              body {
                                margin: 0;
                                padding: 0;
                                font-family: 'Times New Roman', serif;
                              }
                              .page-container {
                                width: 100%;
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                padding-top: 0.10in;
                              }                              .warranty-block {
                                page-break-inside: avoid;
                                margin-bottom: 0.10in;
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                              }
                              .grid-container {
                                display: grid;
                                grid-template-columns: repeat(2, 2.1in);
                                grid-template-rows: repeat(2, auto);
                                border: none;
                                width: 4.2in;
                              }
                              .barcode-item {
                                width: 2.1in;
                                border: 0.1mm dashed #ccc;
                                padding: 3mm;
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                box-sizing: border-box;
                                margin: 0;
                              }
                              @media print {
                                .page-container {
                                  width: 210mm;
                                }
                              }
                            </style>
                          </head>
                          <body>
                            <div class="page-container">
                      `;

                      // Generate barcodes for each warranty
                      warranties.forEach((warranty: any) => {
                        const canvas = document.createElement('canvas');
                        JsBarcode(canvas, warranty.serialNo || 'N/A', {
                          format: "CODE128",
                          width: 2,
                          height: 80,
                          displayValue: true,
                          fontSize: 20,
                          font: "serif"
                        });
                        const barcodeUrl = canvas.toDataURL();

                        const barcodeItemHtml = `
                          <div class="barcode-item">
                            <img src="${barcodeUrl}" style="width: 1.8in; height: auto;" />
                            <div style="font-size: 10px; margin-top: 1mm; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 100%;">${warranty.product?.name || ''}</div>
                          </div>
                        `;

                        allHtml += `
                          <div class="warranty-block">
                            <div class="grid-container">
                              ${barcodeItemHtml}
                              ${barcodeItemHtml}
                              ${barcodeItemHtml}
                              ${barcodeItemHtml}
                            </div>
                          </div>
                        `;
                      });

                      allHtml += `
                            </div>
                            <script>
                              window.onload = () => {
                                setTimeout(() => {
                                  window.print();
                                  window.close();
                                }, 1000);
                              };
                            </script>
                          </body>
                        </html>
                      `;

                      printWindow.document.write(allHtml);
                      printWindow.document.close();
                    }
                  }}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <QrCode size={16} />
                  Print All Barcodes
                </Button>
                <Button 
                  onClick={() => printAllWarrantyCards(invoiceWarranties, settings)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Printer size={16} />
                  Print All Warranties
                </Button>
              </>
            )}            <Button variant="outline" onClick={() => setShowWarrantyModal(false)}>Close</Button>
          </div>        </div>
      </Modal>

      {/* Activity History Modal */}
      <ActivityHistoryModal
        isOpen={showActivityHistoryModal}
        onClose={() => {
          setShowActivityHistoryModal(false);
          setSelectedInvoiceForHistory(null);
        }}
        entityType="deliveryInvoice"
        entityId={selectedInvoiceForHistory?._id || ''}
        entityName={selectedInvoiceForHistory ? `Delivery Invoice ${selectedInvoiceForHistory.invoiceNumber || selectedInvoiceForHistory._id}` : ''}
      />

            {/* Warranty Print Preview Modal */}
            <Modal
              isOpen={showWarrantyPrintPreview}
              onClose={() => {
                setShowWarrantyPrintPreview(false);
                setSelectedWarrantyForPrint(null);
              }}
              title="Warranty Card Print Preview"
              size="xl"
            >
                      <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');

                        .warranty-card-container {
                          width: 4in;
                          height: 6in;
                          margin: 0 auto;
                          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                          overflow: hidden;
                          background: white;
                        }

                        @media print {
                          @page {
                            size: 4in 6in;
                            margin: 0mm !important;
                          }
                          html, body {
                            margin: 0mm !important;
                            padding: 0mm !important;
                            width: 4in !important;
                            height: 6in !important;
                            overflow: hidden !important;
                          }
                          .warranty-card-container {
                            width: 4in !important;
                            height: 6in !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            border: none !important;
                            border-radius: 0 !important;
                            box-shadow: none !important;
                          }
                          .no-print {
                            display: none !important;
                          }
                        }                      `}</style>

                      <div className="flex justify-end mb-4 no-print gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowWarrantyPrintPreview(false);
                            setShowWarrantyModal(true);
                          }}
                        >
                          Back to List
                        </Button>
                        <Button
                          variant="primary"
                          icon={<Printer className="w-4 h-4" />}
                          onClick={async () => {
                            if (selectedWarrantyForPrint) {
                              await printWarrantyCard(selectedWarrantyForPrint, settings);
                            }
                          }}
                        >
                          Print Warranty Card
                        </Button>
                      </div>

                      <div className="bg-gray-100 p-4 flex justify-center overflow-auto max-h-[75vh]">
                        <div
                          id="warranty-card-print-content"
                          className="bg-white shadow-2xl"
                          style={{ padding: 0, margin: 0, width: '4in', height: '6in' }}
                        >
                          <WarrantyCardPrintView warranty={selectedWarrantyForPrint} settings={settings} />
                        </div>
                      </div>                    </Modal>
                              
                                    {/* Add Product Modal */}      <Modal isOpen={showAddProductModal} onClose={() => {
        setShowAddProductModal(false);
        setSelectedParentCategory('');
        setSubCategories([]);
      }} title="Add Product" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Location</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.location} onChange={e => setNewProduct({ ...newProduct, location: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Location</option>
                {locations.map(location => (
                  <option key={location._id} value={location._id}>{location.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('location')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Location" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Parent Category</label>
            <div className="flex items-center space-x-2">
              <select 
                value={selectedParentCategory} 
                onChange={e => handleParentCategoryChange(e.target.value)} 
                className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                style={{ borderRadius: 'var(--border-radius)' } as any}
              >
                <option value="">Select Parent Category</option>
                {rootCategories.map(category => (
                  <option key={category._id} value={category._id}>{category.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('category')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Category" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          
          {selectedParentCategory && subCategories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Sub Category</label>
              <div className="flex items-center space-x-2">
                <select 
                  value={newProduct.category} 
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} 
                  className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" 
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <option value="">Select Sub Category</option>
                  {subCategories.map(category => (
                    <option key={category._id} value={category._id}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <Input label="Product Name" value={newProduct.productName} onChange={e => setNewProduct({ ...newProduct, productName: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Brand</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.brand} onChange={e => updateBarcode({ ...newProduct, brand: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Brand</option>
                {brands.map(brand => (
                  <option key={brand._id} value={brand._id}>{brand.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('brand')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Brand" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          <Input label="Model" value={newProduct.model} onChange={e => updateBarcode({ ...newProduct, model: e.target.value })} />
          <Input label="SKU" value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} />
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Type</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.type} onChange={e => updateBarcode({ ...newProduct, type: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Type</option>
                  {types.map(type => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('type')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Compressor Type</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.compressorType} onChange={e => updateBarcode({ ...newProduct, compressorType: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Compressor Type</option>
                  {compressorTypes.map(compressorType => (
                    <option key={compressorType._id} value={compressorType._id}>{compressorType.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('compressorType')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Compressor Type" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Origin</label>
            <div className="flex items-center space-x-2">
              <select value={newProduct.countryOfOrigin} onChange={e => setNewProduct({ ...newProduct, countryOfOrigin: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <option value="">Select Country of Origin</option>
                {countries.map(country => (
                  <option key={country._id} value={country._id}>{country.name}</option>
                ))}
              </select>
              <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Origin" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          {isAirConditioner && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Country of Assembly</label>
              <div className="flex items-center space-x-2">
                <select value={newProduct.countryOfAssembly} onChange={e => setNewProduct({ ...newProduct, countryOfAssembly: e.target.value })} className="input dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                  <option value="">Select Country of Assembly</option>
                  {countries.map(country => (
                    <option key={country._id} value={country._id}>{country.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded" title="Create Country of Assembly" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
              </div>
            </div>
          )}
          {!isAirConditioner && <Input label="Specifications" value={newProduct.specification} onChange={e => setNewProduct({ ...newProduct, specification: e.target.value })} />}
          {!isAirConditioner && <Input label="Serial No." value={newProduct.serialNo} onChange={e => setNewProduct({ ...newProduct, serialNo: e.target.value })} />}
          {!isAirConditioner && <Input label="Part No." value={newProduct.partNo} onChange={e => setNewProduct({ ...newProduct, partNo: e.target.value })} />}
          <Input label="Details" value={newProduct.details} onChange={e => setNewProduct({ ...newProduct, details: e.target.value })} />
          <Input label="Description" value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} />
          <Input label="Price" type="number" value={newProduct.price} onChange={e => setNewProduct({ ...newProduct, price: e.target.value })} />
          <Input label="Cost Price" type="number" value={newProduct.costPrice} onChange={e => setNewProduct({ ...newProduct, costPrice: e.target.value })} />
          <Input label="Stock Quantity" type="number" value={newProduct.stockQuantity} onChange={e => setNewProduct({ ...newProduct, stockQuantity: e.target.value })} />
          <Input label="Min Stock Level" type="number" value={newProduct.minStockLevel} onChange={e => setNewProduct({ ...newProduct, minStockLevel: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1 font-['Times_New_Roman']">Units</label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1" ref={deliveryAddProductUnitsDropdownRef}>
                <button
                  type="button"
                  onClick={() => setDeliveryAddProductUnitsDropdownOpen(prev => !prev)}
                  className="input w-full flex items-center justify-between dark:bg-[var(--color-surface)] dark:text-[var(--color-on-surface)] text-left"
                  style={{ borderRadius: 'var(--border-radius)' } as any}
                >
                  <span>{newProduct.unitIds?.length ? `${newProduct.unitIds.length} unit(s) selected` : 'Select units'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${deliveryAddProductUnitsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {deliveryAddProductUnitsDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full border border-[var(--color-border)] rounded-lg shadow-lg bg-[var(--color-surface)] max-h-48 overflow-auto" style={{ borderRadius: 'var(--border-radius)' } as any}>
                    {(units || []).filter((u: any) => u.isActive !== false).map((unit: any) => (
                      <label key={unit._id} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--color-surface-light)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newProduct.unitIds?.includes(unit._id) ?? false}
                          onChange={() => {
                            const current = newProduct.unitIds || [];
                            const next = current.includes(unit._id) ? current.filter((id: string) => id !== unit._id) : [...current, unit._id];
                            setNewProduct({ ...newProduct, unitIds: next, unitId: next[0] || newProduct.unitId });
                          }}
                          className="rounded border-[var(--color-border)]"
                        />
                        <span className="text-[var(--color-text)] font-['Times_New_Roman']">{unit.name}</span>
                      </label>
                    ))}
                    {(units || []).filter((u: any) => u.isActive !== false).length === 0 && (
                      <div className="px-3 py-2 text-[var(--color-text-secondary)] text-sm">No units available</div>
                    )}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => handleCreateEntity('unit')} className="px-2 py-1 bg-[var(--color-primary)] text-white rounded shrink-0" title="Create Unit" style={{ borderRadius: 'var(--border-radius)' } as any}>Create</button>
            </div>
          </div>
          
          {/* Hidden canvas for barcode generation */}
          <canvas 
            ref={barcodeCanvasRef} 
            style={{ display: 'none' } as any}
            width="400" 
            height="150"
          />
          
          {/* Barcode Image Display */}
          {barcodeImage && (
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Generated Barcode Image</label>
              <div className="border border-[var(--color-border)] rounded-lg p-4 bg-[var(--color-surface)]" style={{ borderRadius: 'var(--border-radius)' } as any}>
                <img 
                  src={barcodeImage} 
                  alt="Generated Barcode" 
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: '120px' } as any}
                />
                <div className="text-center mt-2">
                  <p className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman'] mb-2">{newProduct.generatedBarcode}</p>
                  <button
                    type="button"
                    onClick={downloadBarcodeImage}
                    className="px-3 py-1 bg-[var(--color-green)] text-[var(--color-white)] rounded text-sm hover:bg-[var(--color-green-dark)] transition-colors"
                  >
                    Download Barcode
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <Input label="Image URL" value={newProduct.image} onChange={e => setNewProduct({ ...newProduct, image: e.target.value })} />
          <button className="btn btn-primary w-full" onClick={handleAddNewProduct}>Add Product</button>
        </div>
      </Modal>

      {/* Create Entity Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={`Create ${createModalType.charAt(0).toUpperCase() + createModalType.slice(1)}`} size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <Input 
            label="Name" 
            value={createModalData.name} 
            onChange={e => setCreateModalData({ ...createModalData, name: e.target.value })} 
            required 
          />
          {(createModalType === 'brand' || createModalType === 'category' || createModalType === 'type' || createModalType === 'compressorType' || createModalType === 'location' || createModalType === 'country' || createModalType === 'unit') && (
            <Input 
              label="Description" 
              value={createModalData.description} 
              onChange={e => setCreateModalData({ ...createModalData, description: e.target.value })} 
            />
          )}
          {createModalType === 'brand' && (
            <Input 
              label="Website" 
              value={createModalData.website} 
              onChange={e => setCreateModalData({ ...createModalData, website: e.target.value })} 
            />
          )}
          {createModalType === 'country' && (
            <>
              <Input 
                label="Code" 
                value={createModalData.code} 
                onChange={e => setCreateModalData({ ...createModalData, code: e.target.value })} 
              />
              <Input 
                label="Region" 
                value={createModalData.region} 
                onChange={e => setCreateModalData({ ...createModalData, region: e.target.value })} 
              />
            </>
          )}
          <div className="flex justify-end space-x-2">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300">Cancel</button>
            <button onClick={handleCreateEntitySubmit} className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded hover:bg-[var(--color-primary-dark)]">Create</button>
          </div>
        </div>
      </Modal>

      {/* Portal-based Product Dropdown */}
      {showProductDropdown && createPortal(
        <div 
          className="fixed z-[9999] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-md shadow-2xl max-h-60 overflow-y-auto"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width
          }}
        >
          {getSearchableProducts().length > 0 ? (
            getSearchableProducts().map(product => (
              <div
                key={product._id}
                onClick={() => handleProductSelect(product)}
                className="product-dropdown-item px-3 py-2 text-xs cursor-pointer hover:bg-[var(--color-surface-hover)] border-b border-[var(--color-border)] last:border-b-0"
                style={{ fontFamily: 'var(--font-family)' } as any}
              >
                <div className="font-semibold text-[var(--color-text)]">{product.name}</div>
                {product.barcode && (
                  <div className="text-[var(--color-text-secondary)] text-xs">Barcode: {product.barcode}</div>
                )}
                {product.sku && (
                  <div className="text-[var(--color-text-secondary)] text-xs">SKU: {product.sku}</div>
                )}
                {product.customFields?.brand && (
                  <div className="text-[var(--color-text-secondary)] text-xs">Brand: {product.customFields.brand}</div>
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">
              No products found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DeliveryInvoicesPage; 